// react and js
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import * as Yup from "yup";
import { useFormik, FormikProvider } from "formik";

// mui
import { Box, Card, Typography, Stack, IconButton, useTheme, useMediaQuery, keyframes, Popover, Select, FormControl } from "@mui/material";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";

// components
import HandleConfirmationDialog from "./actions/HandleConfirmationDialog";
import HandleNewsletter from "./actions/HandleNewsletter";
import HandleImportModule from "./actions/HandleImportModule";
import HandleAddExampleModule from "./actions/HandleAddExampleModule";
import HandlePrintView from "./actions/HandlePrintView";
import HandlePreviewFileContent from "./actions/HandlePreviewFileContent";
import SmartParameter from "../../components/SmartParameter";
import CardHeader from "../../components/CardHeader";

// --
import { TradeBasicsDefaults, TradeBasicsRenderParameters, TradeBasicsConfigure } from "./actions/HandleTradeBasics";
import { InitialStopLossDefaults, InitialStopLossRenderParameters, InitialStopLossConfigure } from "./actions/HandleInitialStopLoss";
import {
  StopLossTrailingsBuildName,
  StopLossTrailingsBuildSubtitle,
  StopLossTrailingsRenderParameters,
  StopLossTrailingsConfigure,
} from "./actions/HandleStopLossTrailings";
import { InitialTakeProfitDefaults, InitialTakeProfitRenderParameters, InitialTakeProfitConfigure } from "./actions/HandleInitialTakeProfit";
import {
  TakeProfitDynamicsBuildName,
  TakeProfitDynamicsBuildSubtitle,
  TakeProfitDynamicsRenderParameters,
  TakeProfitDynamicsConfigure,
} from "./actions/HandleTakeProfitDynamics";
import { SymbolAndTimeframeDefaults, SymbolAndTimeframeRenderParameters, SymbolAndTimeframeConfigure } from "./actions/HandleSymbolAndTimeframe";
import { VolumeCalculationDefaults, VolumeCalculationRenderParameters, VolumeCalculationConfigure } from "./actions/HandleVolumeCalculation";
import {
  PendingOrderSettingsDefaults,
  PendingOrderSettingsRenderParameters,
  PendingOrderSettingsConfigure,
} from "./actions/HandlePendingOrderSettings";
import {
  TradeSplittingsBuildName,
  TradeSplittingsBuildSubtitle,
  TradeSplittingsRenderParameters,
  TradeSplittingsConfigure,
} from "./actions/HandleTradeSplittings";
import { TimingDefaults, TimingRenderParameters, TimingConfigure } from "./actions/HandleTiming";
import { MonitoringDefaults, MonitoringRenderParameters, MonitoringConfigure } from "./actions/HandleMonitoring";
import { DataFeedbackDefaults, DataFeedbackRenderParameters, DataFeedbackConfigure } from "./actions/HandleDataFeedback";
import { MiscellaneousDefaults, MiscellaneousRenderParameters, MiscellaneousConfigure } from "./actions/HandleMiscellaneous";
import { NotificationsDefaults, NotificationsRenderParameters, NotificationsConfigure } from "./actions/HandleNotifications";
//
import { IndicatorsBuildName, IndicatorsBuildSubtitle, IndicatorsRenderParameters, IndicatorsConfigure } from "./actions/HandleIndicators";
import {
  IndicatorCombinationsBuildName,
  IndicatorCombinationsBuildSubtitle,
  IndicatorCombinationsRenderParameters,
  IndicatorCombinationsConfigure,
} from "./actions/HandleIndicatorCombinations";
import {
  ChartAnalyzersBuildName,
  ChartAnalyzersBuildSubtitle,
  ChartAnalyzersRenderParameters,
  ChartAnalyzersConfigure,
} from "./actions/HandleChartAnalyzers";
import { NewsDefaults, NewsRenderParameters, NewsConfigure } from "./actions/HandleNews";
import { CandlePatternsDefaults, CandlePatternsRenderParameters, CandlePatternsConfigure } from "./actions/HandleCandlePatterns";
import { ChartPatternsDefaults, ChartPatternsRenderParameters, ChartPatternsConfigure } from "./actions/HandleChartPatterns";
import { FibonacciPatternsDefaults, FibonacciPatternsRenderParameters, FibonacciPatternsConfigure } from "./actions/HandleFibonacciPatterns";

// hooks
import defines from "../../hooks/defines";

// layouts

// pages

// theme
import {
  B1Tiny,
  B2Tiny,
  B3Tiny,
  ModalContentTextField,
  ModalContentSwitch,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
} from "../../theme/styled";

// utils
import { ModuleDataToTxtData } from "../../utils/moduleConversion";

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const LIMITS = {
  MAX_MODULES: 200,
  MAX_STOP_LOSS_TRAILINGS: 50,
  MAX_TAKE_PROFIT_DYNAMICS: 50,
  MAX_TRADE_SPLITTINGS: 25,
  MAX_INDICATORS: 50,
  MAX_INDICATOR_COMBINATIONS: 100,
  MAX_CHART_ANALYZERS: 25,
};

const STORAGE_KEYS = {
  CURRENT_VERSION: "1.0.0",
  VERSION: "expertAdvisorBuilderVersion",
  DATA: "expertAdvisorBuilderData",
  GUI_STATE: "expertAdvisorBuilderGuiState",
  NEWSLETTER: "expertAdvisorBuilderNewsletter",
};

const NEWSLETTER_DELAY_SECONDS = 40;

const COMPONENTS = [
  "Trade basics",
  "Pending order settings",
  "Signal generation",
  "Initial stop loss",
  "Stop loss trailing",
  "Initial take profit",
  "Dynamic take profit",
  "Symbol and timeframe",
  "Volume calculation",
  "Trade splitting",
  "Timing",
  "Monitoring",
  "Data feedback",
  "Notifications",
  "Miscellaneous",
];

const MARKETING_MESSAGES = [
  "Build your trading strategy with ease",
  "Create professional expert advisors",
  "No coding required - just click and build",
  "Automate your trading ideas today",
  "Design complex strategies simply",
  "From concept to live trading in minutes",
  "Your trading vision, automated",
  "Professional trading at your fingertips",
];

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
function OnboardingArrow({ direction = "arrowLeftUp", style, color = "white" }) {
  const imagePaths = {
    arrowLeftUp: "/illustrations/misc/arrowLeftUp.png",
    arrowRightUp: "/illustrations/misc/arrowRightUp.png",
  };

  // CSS Filter method - works with your current black-on-white PNG
  const getFilterForColor = (targetColor) => {
    if (targetColor === "white" || targetColor === "#ffffff" || targetColor === "#fff") {
      return "invert(1) brightness(2) drop-shadow(0 0 4px rgba(255, 255, 255, 0.8))";
    }
    if (targetColor === "red" || targetColor.includes("255, 0, 0")) {
      return "invert(1) sepia(1) saturate(5) hue-rotate(0deg) drop-shadow(0 0 4px rgba(255, 0, 0, 0.6))";
    }
    if (targetColor === "blue" || targetColor.includes("0, 0, 255")) {
      return "invert(1) sepia(1) saturate(5) hue-rotate(240deg) drop-shadow(0 0 4px rgba(0, 0, 255, 0.6))";
    }
    // Default: try to make it white
    return "invert(1) brightness(2) drop-shadow(0 0 4px rgba(255, 255, 255, 0.8))";
  };

  return (
    <Box
      sx={{
        position: "absolute",
        pointerEvents: "none",
        animation: `${keyframes`0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); }`} 2s ease-in-out infinite`,
        zIndex: 9999,
        width: "100px",
        height: "100px",
        backgroundImage: `url(${imagePaths[direction]})`,
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        // This filter inverts black to white and adds glow
        filter: getFilterForColor(color),
        ...style,
      }}
    />
  );
}

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
function OnboardingMessage({ children, style }) {
  return (
    <Box
      sx={{
        position: "absolute",
        backgroundColor: "rgba(20, 20, 20, 0.95)",
        border: "0px solid rgba(100, 200, 255, 0.5)",
        borderRadius: "10px",
        padding: "16px 24px",
        pointerEvents: "none",
        zIndex: 9998,
        boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.5)", // White shadow
        maxWidth: "600px",
        ...style,
      }}
    >
      <Typography
        sx={{
          fontFamily: "'Kalam', cursive",
          fontSize: "18px",
          color: "rgba(255, 255, 255, 0.95)",
          lineHeight: 1.4,
          textAlign: "center",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const CompareVersions = (version1, version2) => {
  const v1parts = version1.split(".").map(Number);
  const v2parts = version2.split(".").map(Number);

  for (let i = 0; i < Math.max(v1parts.length, v2parts.length); i += 1) {
    const v1part = v1parts[i] || 0;
    const v2part = v2parts[i] || 0;

    if (v1part < v2part) return -1;
    if (v1part > v2part) return 1;
  }
  return 0;
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const ShowUpdateNotification = (previousVersion, currentVersion) => {
  // You can customize this based on your UI framework
  // console.log(`SPA updated from ${previousVersion} to ${currentVersion}`);

  // Example: Show a toast notification or modal
  // toast.info(`Welcome back! SPA has been updated to version ${currentVersion}`);

  // Or set a flag to show update changelog
  localStorage.setItem("expertAdvisorBuilderShowUpdateChangelog", "true");
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const HandleUpdateRoutine = (previousVersion, currentVersion) => {
  // console.log(`Updating from version ${previousVersion} to ${currentVersion}`);

  // Add your update logic here based on version differences

  // Example update routines for different versions
  if (CompareVersions(previousVersion, "1.0.0") < 0) {
    // console.log("Running update routine for version 1.0.0");
    // Add specific update logic for version 1.0.0
    // e.g., migrate data structure, clear old cache, etc.
  }

  if (CompareVersions(previousVersion, "1.1.0") < 0) {
    // console.log("Running update routine for version 1.1.0");
    // Add specific update logic for version 1.1.0
  }

  // You can add more version-specific updates here

  // Clear old data if needed
  // localStorage.removeItem('old_data_key');

  // Show user notification about updates
  ShowUpdateNotification(previousVersion, currentVersion);
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const CheckSPAVersion = () => {
  try {
    const storedVersion = localStorage.getItem(STORAGE_KEYS.VERSION);

    if (!storedVersion) {
      // First time user or no version stored
      // console.log("First time user - initializing version tracking");
      localStorage.setItem(STORAGE_KEYS.VERSION, STORAGE_KEYS.CURRENT_VERSION);
      return {
        isFirstTime: true,
        needsUpdate: false,
        previousVersion: null,
        currentVersion: STORAGE_KEYS.CURRENT_VERSION,
      };
    }

    // Compare stored version with current version
    const versionComparison = CompareVersions(storedVersion, STORAGE_KEYS.CURRENT_VERSION);

    if (versionComparison < 0) {
      // Stored version is older - needs update
      // console.log(`Version update needed: ${storedVersion} -> ${STORAGE_KEYS.CURRENT_VERSION}`);

      // Run update routine
      HandleUpdateRoutine(storedVersion, STORAGE_KEYS.CURRENT_VERSION);

      // Update stored version
      localStorage.setItem(STORAGE_KEYS.VERSION, STORAGE_KEYS.CURRENT_VERSION);

      return {
        isFirstTime: false,
        needsUpdate: true,
        previousVersion: storedVersion,
        currentVersion: STORAGE_KEYS.CURRENT_VERSION,
      };
      // eslint-disable-next-line
    } else if (versionComparison > 0) {
      // Stored version is newer (shouldn't happen in normal use)
      console.warn("Stored version is newer than current version - possible rollback");
      localStorage.setItem(STORAGE_KEYS.VERSION, STORAGE_KEYS.CURRENT_VERSION);

      return {
        isFirstTime: false,
        needsUpdate: false,
        previousVersion: storedVersion,
        currentVersion: STORAGE_KEYS.CURRENT_VERSION,
      };
    } else {
      // Versions match - no update needed
      // console.log("SPA version is up to date");
      return {
        isFirstTime: false,
        needsUpdate: false,
        previousVersion: storedVersion,
        currentVersion: STORAGE_KEYS.CURRENT_VERSION,
      };
    }
  } catch (error) {
    console.error("Error checking SPA version:", error);
    // Fallback: assume update is needed
    localStorage.setItem(STORAGE_KEYS.VERSION, STORAGE_KEYS.CURRENT_VERSION);
    return {
      isFirstTime: false,
      needsUpdate: true,
      previousVersion: "unknown",
      currentVersion: STORAGE_KEYS.CURRENT_VERSION,
    };
  }
};

// ----------------------------------------------------------------------
// Properties
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function ExpertAdvisorBuilder() {
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const generateRandomPosition = () => {
    const topPercent = Math.floor(Math.random() * 30) + 60;
    const leftPercent = Math.floor(Math.random() * 40) + 50;
    return { top: `${topPercent}vh`, left: `${leftPercent}vw` };
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const downloadTextFile = (content, filename) => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const exportModule = (moduleId) => {
    const moduleData = superDataStructure.find((m) => m.moduleId === moduleId);
    const TxtData = ModuleDataToTxtData(moduleData);
    const moduleNumber = moduleData.basics.nNumber;
    const fileName = `Module${moduleNumber}.txt`;

    downloadTextFile(TxtData, fileName);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [superDataStructure, setSuperDataStructure] = useState([]);
  const isLoadingFromStorage = useRef(false);
  const importModuleRef = useRef(null);
  //
  const stopLossTrailingConfigureRef = useRef(null);
  const takeProfitDynamicConfigureRef = useRef(null);
  const tradeSplittingConfigureRef = useRef(null);
  //
  const indicatorConfigureRef = useRef(null);
  const indicatorCombinationConfigureRef = useRef(null);
  const chartAnalyzerConfigureRef = useRef(null);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [loadingSpinner, setLoadingSpinner] = useState(true);
  const [professionalMode, setProfessionalMode] = useState(true);
  const [duplicateAction, setDuplicateAction] = useState("skip");
  const [showSessionRestoredMessage, setShowSessionRestoredMessage] = useState(false);
  const [showOnboardingStep1, setShowOnboardingStep1] = useState(true);
  const [showOnboardingStep2, setShowOnboardingStep2] = useState(false);
  const [canvasDragActive, setCanvasDragActive] = useState(false);
  const [, setCanvasDragCounter] = useState(0);
  const [pendingImportFiles, setPendingImportFiles] = useState([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldShowMessage, setShouldShowMessage] = useState(true);
  const [messagePosition, setMessagePosition] = useState(generateRandomPosition());
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    disabled: false,
  });
  const [showNewsletterDialog, setShowNewsletterDialog] = useState(false);

  // Popover states for different item types
  const [modulePopover, setModulePopover] = useState({ anchorEl: null, itemId: null });
  //
  const [stopLossTrailingPopover, setStopLossTrailingPopover] = useState({ anchorEl: null, itemId: null });
  const [takeProfitDynamicPopover, setTakeProfitDynamicPopover] = useState({ anchorEl: null, itemId: null });
  const [tradeSplittingPopover, setTradeSplittingPopover] = useState({ anchorEl: null, itemId: null });
  //
  const [indicatorPopover, setIndicatorPopover] = useState({ anchorEl: null, itemId: null });
  const [indicatorCombinationPopover, setIndicatorCombinationPopover] = useState({ anchorEl: null, itemId: null });
  const [chartAnalyzerPopover, setChartAnalyzerPopover] = useState({ anchorEl: null, itemId: null });

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [moduleStates, setModuleStates] = useState({});
  const [componentStates, setComponentStates] = useState({});
  const [tier3States, setTier3States] = useState({});
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(true);
  const [hasEverAddedModule, setHasEverAddedModule] = useState(false);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [modules, setModules] = useState([]);
  //
  const [state_nTradingState, setState_nTradingState] = useState("1");
  const [state_nNumber, setState_nNumber] = useState(1);
  const [state_dVersion, setState_dVersion] = useState("3.1");
  const [state_strIdeaOutputDescription, setState_strIdeaOutputDescription] = useState("");
  //
  const [tradeBasics, setTradeBasics] = useState({});
  const [initialStopLoss, setInitialStopLoss] = useState({});
  const [stopLossTrailings, setStopLossTrailings] = useState([]);
  const [initialTakeProfit, setInitialTakeProfit] = useState({});
  const [takeProfitDynamics, setTakeProfitDynamics] = useState([]);
  const [symbolAndTimeframe, setSymbolAndTimeframe] = useState({});
  const [volumeCalculation, setVolumeCalculation] = useState({});
  const [pendingOrderSettings, setPendingOrderSettings] = useState({});
  const [tradeSplittings, setTradeSplittings] = useState([]);
  const [timing, setTiming] = useState({});
  const [monitoring, setMonitoring] = useState({});
  const [dataFeedback, setDataFeedback] = useState({});
  const [miscellaneous, setMiscellaneous] = useState({});
  const [notifications, setNotifications] = useState({});
  //
  const [indicators, setIndicators] = useState([]);
  const [indicatorCombinations, setIndicatorCombinations] = useState([]);
  const [chartAnalyzers, setChartAnalyzers] = useState([]);
  const [news, setNews] = useState({});
  const [candlePatterns, setCandlePatterns] = useState({});
  const [chartPatterns, setChartPatterns] = useState({});
  const [fibonacciPatterns, setFibonacciPatterns] = useState({});

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedTier3, setSelectedTier3] = useState(null);
  const [selectedTier4, setSelectedTier4] = useState(null);
  //
  const [selectedStopLossTrailing, setSelectedStopLossTrailing] = useState(null);
  const [selectedTakeProfitDynamic, setSelectedTakeProfitDynamic] = useState(null);
  const [selectedTradeSplitting, setSelectedTradeSplitting] = useState(null);
  //
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [selectedIndicatorCombination, setSelectedIndicatorCombination] = useState(null);
  const [selectedChartAnalyzer, setSelectedChartAnalyzer] = useState(null);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const getStopLossTrailingConfig = (stopLossTrailingId) => {
    const stopLossTrailing = stopLossTrailings.find((ind) => ind.id === stopLossTrailingId);
    return stopLossTrailing?.config || null;
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const getTakeProfitDynamicConfig = (takeProfitDynamicId) => {
    const takeProfitDynamic = takeProfitDynamics.find((ind) => ind.id === takeProfitDynamicId);
    return takeProfitDynamic?.config || null;
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const getTradeSplittingConfig = (tradeSplittingId) => {
    const tradeSplitting = tradeSplittings.find((ind) => ind.id === tradeSplittingId);
    return tradeSplitting?.config || null;
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const getIndicatorConfig = (indicatorId) => {
    const indicator = indicators.find((ind) => ind.id === indicatorId);
    if(indicator && indicator.config !== null)
    {
      // Check for dependecies
      const dependencies = [];

      if (pendingOrderSettings.nPrice === "2" && parseInt(pendingOrderSettings.nInosUsedForValueInjection, 10) === indicator.id) {
        dependencies.push("Pending order settings");
      }

      if (initialStopLoss.nSelection === "3" && parseInt(initialStopLoss.nInosUsedForValueInjection, 10) === indicator.id) {
        dependencies.push("Initial stop loss");
      }

      stopLossTrailings.forEach((stopLossTrailing) => {
        if (stopLossTrailing.config !== undefined && stopLossTrailing.config !== null) {
          if (stopLossTrailing.config.nType === "4") {
            if (parseInt(stopLossTrailing.config.nInosUsedForValueInjection, 10) === indicator.id) {
              dependencies.push(`Stop loss trailing -${stopLossTrailing?.name || 'Unnamed'} - ${stopLossTrailing?.subtitle || ''}`);
            }
          }
        }
      });

      if (initialTakeProfit.nSelection === "4" && parseInt(initialTakeProfit.nInosUsedForValueInjection, 10) === indicator.id) {
        dependencies.push("Initial take profit");
      }

      takeProfitDynamics.forEach((takeProfitDynamic) => {
        if (takeProfitDynamic.config !== undefined && takeProfitDynamic.config !== null) {
          if (takeProfitDynamic.config.nType === "4") {
            if (parseInt(takeProfitDynamic.config.nInosUsedForValueInjection, 10) === indicator.id) {
              dependencies.push(`Dynamic take profit -${takeProfitDynamic?.name || 'Unnamed'} - ${takeProfitDynamic?.subtitle || ''}`);
            }
          }
        }
      });

      indicatorCombinations.forEach((indicatorCombination) => {
        if (indicatorCombination.config !== undefined && indicatorCombination.config !== null) {
          if (parseInt(indicatorCombination.config.nInputInos1, 10) === indicator.id) {
            dependencies.push(`Indicator Combination - ${indicatorCombination?.name || 'Unnamed'} - ${indicatorCombination?.subtitle || ''}`);
          }
          if (parseInt(indicatorCombination.config.nInputInos2, 10) === indicator.id) {
            dependencies.push(`Indicator Combination - ${indicatorCombination?.name || 'Unnamed'} - ${indicatorCombination?.subtitle || ''}`);
          }
        }
      });

      if (volumeCalculation.nVolumeCalculation === "3" && parseInt(volumeCalculation.nInosUsedForValueInjection, 10) === indicator.id) {
        dependencies.push("Volume calculation");
      }

      if (dependencies.length > 0) 
      {
        indicator.config.forwardUsed = true;
      }
      else
      {
        indicator.config.forwardUsed = false;
      }
    }
    return indicator?.config || null;
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const getIndicatorCombinationConfig = (indicatorCombinationId) => {
    const indicatorCombination = indicatorCombinations.find((ind) => ind.id === indicatorCombinationId);
    return indicatorCombination?.config || null;
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const getChartAnalyzerConfig = (chartAnalyzerId) => {
    const chartAnalyzer = chartAnalyzers.find((ind) => ind.id === chartAnalyzerId);
    if(chartAnalyzer && chartAnalyzer.config !== null)
    {
      // Check for dependecies
      const dependencies = [];

      if (pendingOrderSettings.nPrice === "3" && parseInt(pendingOrderSettings.nCaSupResUsedForValueInjection, 10) === chartAnalyzer.id) {
        dependencies.push("Pending order settings");
      }

      if (initialStopLoss.nSelection === "4" && parseInt(initialStopLoss.nCaSupResUsedForValueInjection, 10) === chartAnalyzer.id) {
        dependencies.push("Initial stop loss");
      }

      stopLossTrailings.forEach((stopLossTrailing) => {
        if (stopLossTrailing.config !== undefined && stopLossTrailing.config !== null) {
          if (stopLossTrailing.config.nType === "5") {
            if (parseInt(stopLossTrailing.config.nCaSupResUsedForValueInjection, 10) === chartAnalyzer.id) {
              dependencies.push(`Stop loss trailing -${stopLossTrailing?.name || 'Unnamed'} - ${stopLossTrailing?.subtitle || ''}`);
            }
          }
        }
      });

      if (initialTakeProfit.nSelection === "5" && parseInt(initialTakeProfit.nCaSupResUsedForValueInjection, 10) === chartAnalyzer.id) {
        dependencies.push("Initial take profit");
      }

      takeProfitDynamics.forEach((takeProfitDynamic) => {
        if (takeProfitDynamic.config !== undefined && takeProfitDynamic.config !== null) {
          if (takeProfitDynamic.config.nType === "5") {
            if (parseInt(takeProfitDynamic.config.nCaSupResUsedForValueInjection, 10) === chartAnalyzer.id) {
              dependencies.push(`Dynamic take profit -${takeProfitDynamic?.name || 'Unnamed'} - ${takeProfitDynamic?.subtitle || ''}`);
            }
          }
        }
      });

      if (dependencies.length > 0) 
      {
        chartAnalyzer.config.forwardUsed = true;
      }
      else
      {
        chartAnalyzer.config.forwardUsed = false;
      }
    }
    return chartAnalyzer?.config || null;
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const saveStopLossTrailingConfig = (stopLossTrailingId, configData) => {
    setStopLossTrailings((prev) =>
      prev.map((stopLossTrailing) => (stopLossTrailing.id === stopLossTrailingId ? { ...stopLossTrailing, config: configData } : stopLossTrailing))
    );
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const saveTakeProfitDynamicConfig = (takeProfitDynamicId, configData) => {
    setTakeProfitDynamics((prev) =>
      prev.map((takeProfitDynamic) =>
        takeProfitDynamic.id === takeProfitDynamicId ? { ...takeProfitDynamic, config: configData } : takeProfitDynamic
      )
    );
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const saveTradeSplittingConfig = (tradeSplittingId, configData) => {
    setTradeSplittings((prev) =>
      prev.map((tradeSplitting) => (tradeSplitting.id === tradeSplittingId ? { ...tradeSplitting, config: configData } : tradeSplitting))
    );
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const saveIndicatorConfig = (indicatorId, configData) => {
    setIndicators((prev) => prev.map((indicator) => (indicator.id === indicatorId ? { ...indicator, config: configData } : indicator)));
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const saveIndicatorCombinationConfig = (indicatorCombinationId, configData) => {
    setIndicatorCombinations((prev) =>
      prev.map((indicatorCombination) =>
        indicatorCombination.id === indicatorCombinationId ? { ...indicatorCombination, config: configData } : indicatorCombination
      )
    );
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const saveChartAnalyzerConfig = (chartAnalyzerId, configData) => {
    setChartAnalyzers((prev) =>
      prev.map((chartAnalyzer) => (chartAnalyzer.id === chartAnalyzerId ? { ...chartAnalyzer, config: configData } : chartAnalyzer))
    );
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const getModuleKey = (moduleId) => `module_${moduleId}`;

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const getComponentKey = (moduleId, component) => `${moduleId}_${component}`;

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const getTier3Key = (moduleId, component, tier3) => `${moduleId}_${component}_${tier3}`;

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (modules.length > 0) {
      setShowOnboardingStep1(false);
      if (showOnboardingStep1 === true) {
        setShowOnboardingStep2(true);
      }
    }
  }, [modules.length]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const loadModuleBasics = useCallback(
    (moduleId) => {
      let moduleData = superDataStructure.find((m) => m.moduleId === moduleId);

      if (moduleData === undefined) {
        const savedData = localStorage.getItem(STORAGE_KEYS.DATA);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          moduleData = parsedData.find((m) => m.moduleId === moduleId);
        }
      }

      if (moduleData) {
        //
        if (moduleData.basics) {
          setState_nTradingState(moduleData.basics.nTradingState || "1");
          setState_nNumber(moduleData.basics.nNumber || 1);
          setState_dVersion(moduleData.basics.dVersion || "3.1");
          setState_strIdeaOutputDescription(moduleData.basics.strIdeaOutputDescription || "");
        }
        //
        if (moduleData.tradeBasics) {
          setTradeBasics(moduleData.tradeBasics);
        } else {
          setTradeBasics(TradeBasicsDefaults);
        }
        if (moduleData.initialStopLoss) {
          setInitialStopLoss(moduleData.initialStopLoss);
        } else {
          setInitialStopLoss(InitialStopLossDefaults);
        }
        if (moduleData.stopLossTrailings) {
          setStopLossTrailings([...moduleData.stopLossTrailings]);
        } else {
          setStopLossTrailings([]);
        }
        if (moduleData.initialTakeProfit) {
          setInitialTakeProfit(moduleData.initialTakeProfit);
        } else {
          setInitialTakeProfit(InitialTakeProfitDefaults);
        }
        if (moduleData.takeProfitDynamics) {
          setTakeProfitDynamics([...moduleData.takeProfitDynamics]);
        } else {
          setTakeProfitDynamics([]);
        }
        if (moduleData.symbolAndTimeframe) {
          setSymbolAndTimeframe(moduleData.symbolAndTimeframe);
        } else {
          setSymbolAndTimeframe(SymbolAndTimeframeDefaults);
        }
        if (moduleData.volumeCalculation) {
          setVolumeCalculation(moduleData.volumeCalculation);
        } else {
          setVolumeCalculation(VolumeCalculationDefaults);
        }
        if (moduleData.pendingOrderSettings) {
          setPendingOrderSettings(moduleData.pendingOrderSettings);
        } else {
          setPendingOrderSettings(PendingOrderSettingsDefaults);
        }
        if (moduleData.tradeSplittings) {
          setTradeSplittings([...moduleData.tradeSplittings]);
        } else {
          setTradeSplittings([]);
        }
        if (moduleData.timing) {
          setTiming(moduleData.timing);
        } else {
          setTiming(TimingDefaults);
        }
        if (moduleData.monitoring) {
          setMonitoring(moduleData.monitoring);
        } else {
          setMonitoring(MonitoringDefaults);
        }
        if (moduleData.dataFeedback) {
          setDataFeedback(moduleData.dataFeedback);
        } else {
          setDataFeedback(DataFeedbackDefaults);
        }
        if (moduleData.miscellaneous) {
          setMiscellaneous(moduleData.miscellaneous);
        } else {
          setMiscellaneous(MiscellaneousDefaults);
        }
        if (moduleData.notifications) {
          setNotifications(moduleData.notifications);
        } else {
          setNotifications(NotificationsDefaults);
        }
        //
        if (moduleData.indicators) {
          setIndicators([...moduleData.indicators]);
        } else {
          setIndicators([]);
        }
        if (moduleData.indicatorCombinations) {
          setIndicatorCombinations([...moduleData.indicatorCombinations]);
        } else {
          setIndicatorCombinations([]);
        }
        if (moduleData.chartAnalyzers) {
          setChartAnalyzers([...moduleData.chartAnalyzers]);
        } else {
          setChartAnalyzers([]);
        }
        if (moduleData.news) {
          setNews(moduleData.news);
        } else {
          setNews(NewsDefaults);
        }
        if (moduleData.candlePatterns) {
          setCandlePatterns(moduleData.candlePatterns);
        } else {
          setCandlePatterns(CandlePatternsDefaults);
        }
        if (moduleData.chartPatterns) {
          setChartPatterns(moduleData.chartPatterns);
        } else {
          setChartPatterns(ChartPatternsDefaults);
        }
        if (moduleData.fibonacciPatterns) {
          setFibonacciPatterns(moduleData.fibonacciPatterns);
        } else {
          setFibonacciPatterns(FibonacciPatternsDefaults);
        }
      }
    },
    [superDataStructure]
  );

  // ----------------------------------------------------------------------
  // Formik value validation
  // ----------------------------------------------------------------------
  const formik = useFormik({
    // ----------------------------------------------------------------------
    // Initial values
    // ----------------------------------------------------------------------
    initialValues: {
      // ----------------------------------------------------------------------
      // Content
      // ----------------------------------------------------------------------
      nTradingState: state_nTradingState || "1",
      nNumber: state_nNumber || 1,
      strIdeaOutputDescription: state_strIdeaOutputDescription,
    },
    // ----------------------------------------------------------------------
    // Validation
    // ----------------------------------------------------------------------
    validationSchema: Yup.object().shape({
      // ----------------------------------------------------------------------
      // Content
      // ----------------------------------------------------------------------
      nTradingState: Yup.string().required("Trading state is required"),
      nNumber: Yup.number()
        .required("Number is required")
        .positive()
        .integer()
        .min(1, "Number must be at least 1")
        .max(9999999999999, "Number must be at most 9999999999999"),
      strIdeaOutputDescription: Yup.string()
        .trim()
        .max(64, "Description must be at most 64 characters")
        .matches(/\S/, "Description cannot be only whitespace")
        .matches(/^[^=_]*$/, "Description cannot contain '=' or '_' characters"),
    }),
  });
  const { getFieldProps, touched, errors, setFieldValue, resetForm, setFieldTouched, setErrors, setTouched } = formik;

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleTradingModuleSelection = (identifier) => {
    if (identifier !== selectedModule) {
      // Clear errors and touched states
      setErrors({});
      setTouched({});

      // If switching to a different module
      if (identifier !== null) {
        resetForm({
          values: {
            // ---
            nTradingState: "1",
            nNumber: 1,
            strIdeaOutputDescription: "",
            // ---
          },
        });
      }
    }

    const selectedModuleData = modules.find((module) => module.id === identifier);
    const alreadyOpened = selectedModuleData ? selectedModuleData.alreadyOpened : null;

    // Check if we're deselecting the current module
    const isDeselecting = identifier === selectedModule;

    // Always save current state before any change (if there's a currently selected module)
    if (selectedModule !== null) {
      const currentModuleKey = getModuleKey(selectedModule);
      setModuleStates((prev) => ({
        ...prev,
        [currentModuleKey]: {
          selectedComponent,
          selectedTier3,
          selectedTier4,
          //
          selectedStopLossTrailing,
          selectedTakeProfitDynamic,
          selectedTradeSplitting,
          //
          selectedIndicator,
          selectedIndicatorCombination,
          selectedChartAnalyzer,
        },
      }));
    }

    // Handle first-time opening logic
    if (!isDeselecting && alreadyOpened === false) {
      setSelectedComponent("Signal generation");
      setSelectedTier3("Indicators");
      // Clear other selections for first-time opening
      setSelectedTier4(null);
      //
      setSelectedStopLossTrailing(null);
      setSelectedTakeProfitDynamic(null);
      setSelectedTradeSplitting(null);
      //
      setSelectedIndicator(null);
      setSelectedIndicatorCombination(null);
      setSelectedChartAnalyzer(null);
    } else if (!isDeselecting && identifier !== selectedModule) {
      // Restore previous state for this module
      const moduleKey = getModuleKey(identifier);
      const savedState = moduleStates[moduleKey];

      if (savedState) {
        setSelectedComponent(savedState.selectedComponent);
        setSelectedTier3(savedState.selectedTier3);
        setSelectedTier4(savedState.selectedTier4);
        //
        setSelectedStopLossTrailing(savedState.selectedStopLossTrailing);
        setSelectedTakeProfitDynamic(savedState.selectedTakeProfitDynamic);
        setSelectedTradeSplitting(savedState.selectedTradeSplitting);
        //
        setSelectedIndicator(savedState.selectedIndicator);
        setSelectedIndicatorCombination(savedState.selectedIndicatorCombination);
        setSelectedChartAnalyzer(savedState.selectedChartAnalyzer);
      } else {
        // No previous state, clear all selections
        setSelectedComponent(null);
        setSelectedTier3(null);
        setSelectedTier4(null);
        //
        setSelectedStopLossTrailing(null);
        setSelectedTakeProfitDynamic(null);
        setSelectedTradeSplitting(null);
        //
        setSelectedIndicator(null);
        setSelectedIndicatorCombination(null);
        setSelectedChartAnalyzer(null);
      }
    } else if (isDeselecting) {
      // Module is being deselected, clear all lower-tier selections
      setSelectedComponent(null);
      setSelectedTier3(null);
      setSelectedTier4(null);
      //
      setSelectedStopLossTrailing(null);
      setSelectedTakeProfitDynamic(null);
      setSelectedTradeSplitting(null);
      //
      setSelectedIndicator(null);
      setSelectedIndicatorCombination(null);
      setSelectedChartAnalyzer(null);
    }

    // Set alreadyOpened to true when module is opened for the first time
    if (!isDeselecting && selectedModuleData && !selectedModuleData.alreadyOpened) {
      setModules((prev) => prev.map((module) => (module.id === identifier ? { ...module, alreadyOpened: true } : module)));
    }

    setSelectedModule(identifier === selectedModule ? null : identifier);

    // Hide onboarding step 2
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }

    // Add this to your existing handleTradingModuleSelection function after setting selectedModule
    if (identifier !== selectedModule && identifier !== null) {
      loadModuleBasics(identifier);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleComponentSelection = (identifier) => {
    // Save current component state before switching
    if (selectedComponent !== null && selectedComponent !== identifier && selectedModule) {
      const componentKey = getComponentKey(selectedModule, selectedComponent);
      setComponentStates((prev) => ({
        ...prev,
        [componentKey]: {
          selectedTier3,
          selectedTier4,
          //
          selectedStopLossTrailing,
          selectedTakeProfitDynamic,
          selectedTradeSplitting,
          //
          selectedIndicator,
          selectedIndicatorCombination,
          selectedChartAnalyzer,
        },
      }));
    }

    // Check if we're deselecting the current component
    const isDeselecting = identifier === selectedComponent;

    setSelectedComponent(identifier === selectedComponent ? null : identifier);

    // Hide onboarding step 2 when user manually interacts
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }

    // If deselecting, clear all lower tier selections
    if (isDeselecting) {
      setSelectedTier3(null);
      setSelectedTier4(null);
      //
      setSelectedStopLossTrailing(null);
      setSelectedTakeProfitDynamic(null);
      setSelectedTradeSplitting(null);
      //
      setSelectedIndicator(null);
      setSelectedIndicatorCombination(null);
      setSelectedChartAnalyzer(null);
    } else if (identifier !== selectedComponent && identifier !== null && selectedModule) {
      // If selecting a different component, restore its previous state
      const componentKey = getComponentKey(selectedModule, identifier);
      const savedState = componentStates[componentKey];

      if (savedState) {
        setSelectedTier3(savedState.selectedTier3);
        setSelectedTier4(savedState.selectedTier4);
        //
        setSelectedStopLossTrailing(savedState.selectedStopLossTrailing);
        setSelectedTakeProfitDynamic(savedState.selectedTakeProfitDynamic);
        setSelectedTradeSplitting(savedState.selectedTradeSplitting);
        //
        setSelectedIndicator(savedState.selectedIndicator);
        setSelectedIndicatorCombination(savedState.selectedIndicatorCombination);
        setSelectedChartAnalyzer(savedState.selectedChartAnalyzer);
      } else {
        // No previous state for this component, clear tier3+ selections
        setSelectedTier3(null);
        setSelectedTier4(null);
        //
        setSelectedStopLossTrailing(null);
        setSelectedTakeProfitDynamic(null);
        setSelectedTradeSplitting(null);
        //
        setSelectedIndicator(null);
        setSelectedIndicatorCombination(null);
        setSelectedChartAnalyzer(null);
      }
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleTier3Selection = (identifier) => {
    // Save current tier3 state before switching
    if (selectedTier3 !== null && selectedTier3 !== identifier && selectedModule && selectedComponent) {
      const tier3Key = getTier3Key(selectedModule, selectedComponent, selectedTier3);
      setTier3States((prev) => ({
        ...prev,
        [tier3Key]: {
          selectedStopLossTrailing,
          selectedTakeProfitDynamic,
          selectedTradeSplitting,
          //
          selectedIndicator,
          selectedIndicatorCombination,
          selectedChartAnalyzer,
        },
      }));
    }

    // Check if we're deselecting the current tier3
    const isDeselecting = identifier === selectedTier3;

    setSelectedTier3(identifier === selectedTier3 ? null : identifier);

    // Hide onboarding step 2 when user manually interacts
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }

    // If deselecting, clear all tier4+ selections
    if (isDeselecting) {
      setSelectedStopLossTrailing(null);
      setSelectedTakeProfitDynamic(null);
      setSelectedTradeSplitting(null);
      //
      setSelectedIndicator(null);
      setSelectedIndicatorCombination(null);
      setSelectedChartAnalyzer(null);
    } else if (identifier !== selectedTier3 && identifier !== null && selectedModule && selectedComponent) {
      // If selecting a different tier3, restore its previous state
      const tier3Key = getTier3Key(selectedModule, selectedComponent, identifier);
      const savedState = tier3States[tier3Key];

      if (savedState) {
        setSelectedStopLossTrailing(savedState.selectedStopLossTrailing);
        setSelectedTakeProfitDynamic(savedState.selectedTakeProfitDynamic);
        setSelectedTradeSplitting(savedState.selectedTradeSplitting);
        //
        setSelectedIndicator(savedState.selectedIndicator);
        setSelectedIndicatorCombination(savedState.selectedIndicatorCombination);
        setSelectedChartAnalyzer(savedState.selectedChartAnalyzer);
      } else {
        // No previous state for this tier3, clear tier4+ selections
        setSelectedStopLossTrailing(null);
        setSelectedTakeProfitDynamic(null);
        setSelectedTradeSplitting(null);
        //
        setSelectedIndicator(null);
        setSelectedIndicatorCombination(null);
        setSelectedChartAnalyzer(null);
      }
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleStopLossTrailingSelection = (identifier) => {
    setSelectedStopLossTrailing(identifier === selectedStopLossTrailing ? null : identifier);
    // Hide onboarding step 2
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleTakeProfitDynamicSelection = (identifier) => {
    setSelectedTakeProfitDynamic(identifier === selectedTakeProfitDynamic ? null : identifier);
    // Hide onboarding step 2
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleTradeSplittingSelection = (identifier) => {
    setSelectedTradeSplitting(identifier === selectedTradeSplitting ? null : identifier);
    // Hide onboarding step 2
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleIndicatorSelection = (identifier) => {
    setSelectedIndicator(identifier === selectedIndicator ? null : identifier);
    // Hide onboarding step 2
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleIndicatorCombinationSelection = (identifier) => {
    setSelectedIndicatorCombination(identifier === selectedIndicatorCombination ? null : identifier);
    // Hide onboarding step 2
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleChartAnalyzerSelection = (identifier) => {
    setSelectedChartAnalyzer(identifier === selectedChartAnalyzer ? null : identifier);
    // Hide onboarding step 2
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const addModule = (moduleData = null, newModuleId = true, newModuleNumber = true) => {
    // Add limit check at the beginning
    if (modules.length >= LIMITS.MAX_MODULES) {
      return;
    }

    // Reset formik errors
    setErrors({});
    setTouched({});

    let moduleId;
    let moduleTradingState;
    let moduleNumber;
    let moduleDescription;
    let moduleName;

    // Handle Module ID
    if (newModuleId) {
      // Generate new ID
      moduleId = Date.now();
    } else {
      // Use existing ID from moduleData
      moduleId = moduleData?.moduleId;
    }

    // Handle Module Trading State
    if (moduleData && moduleData.basics) {
      moduleTradingState = moduleData.basics.nTradingState || "1";
    } else {
      moduleTradingState = "1";
    }

    // Handle Module Number
    if (newModuleNumber) {
      // Generate new unique module number
      moduleNumber = null;
      while (moduleNumber === null) {
        let isUnique = true;
        const moduleNumberTest = Math.floor(Math.random() * (9999 - 1000 + 1) + 1000);
        modules.forEach((module) => {
          // Updated to extract number from new format
          const match = module?.name?.match(/Module (\d+)/);
          const existingNumber = match ? parseInt(match[1], 10) : null;
          if (moduleNumberTest === existingNumber) {
            isUnique = false;
          }
        });
        if (isUnique) {
          moduleNumber = moduleNumberTest;
        }
      }
    } else {
      // Use existing number from moduleData
      moduleNumber = moduleData?.basics?.nNumber || Math.floor(Math.random() * (9999 - 1000 + 1) + 1000);
    }

    // Handle Module Description and Name
    if (moduleData && moduleData.basics) {
      moduleDescription = moduleData.basics.strIdeaOutputDescription || "";
    } else {
      moduleDescription = "";
    }

    // Build module name
    if (moduleData?.moduleName && !newModuleNumber) {
      // Use existing module name if preserving data and not generating new number
      moduleName = moduleData.moduleName;
    } else {
      // Build new name based on number and description
      moduleName = moduleDescription !== "" ? `Module ${moduleNumber}: ${moduleDescription}` : `Module ${moduleNumber}`;
    }

    // Save current module state before switching (if there's a currently selected module)
    if (selectedModule !== null) {
      const currentModuleKey = getModuleKey(selectedModule);
      setModuleStates((prev) => ({
        ...prev,
        [currentModuleKey]: {
          selectedComponent,
          selectedTier3,
          selectedTier4,
          //
          selectedStopLossTrailing,
          selectedTakeProfitDynamic,
          selectedTradeSplitting,
          //
          selectedIndicator,
          selectedIndicatorCombination,
          selectedChartAnalyzer,
        },
      }));
    }

    // Set the new module as selected with default navigation path
    setSelectedModule(moduleId);
    setSelectedComponent("Signal generation");
    setSelectedTier3("Indicators");

    // Clear all other tier selections for the new module
    setSelectedTier4(null);
    //
    setSelectedStopLossTrailing(null);
    setSelectedTakeProfitDynamic(null);
    setSelectedTradeSplitting(null);
    //
    setSelectedIndicator(null);
    setSelectedIndicatorCombination(null);
    setSelectedChartAnalyzer(null);

    // Set basic module information
    setState_nTradingState(moduleTradingState);
    setState_nNumber(moduleNumber);
    setState_dVersion(moduleData?.basics?.dVersion || "3.1");
    setState_strIdeaOutputDescription(moduleDescription);

    let initialTradeBasicsConfig = {};
    if (moduleData && moduleData.tradeBasics) {
      initialTradeBasicsConfig = moduleData.tradeBasics;
    } else {
      initialTradeBasicsConfig = TradeBasicsDefaults;
    }
    setTradeBasics(initialTradeBasicsConfig);

    let initialInitialStopLossConfig = {};
    if (moduleData && moduleData.initialStopLoss) {
      initialInitialStopLossConfig = moduleData.initialStopLoss;
    } else {
      initialInitialStopLossConfig = InitialStopLossDefaults;
    }
    setInitialStopLoss(initialInitialStopLossConfig);

    let initialStopLossTrailings = [];
    if (moduleData && moduleData.stopLossTrailings && Array.isArray(moduleData.stopLossTrailings)) {
      initialStopLossTrailings = moduleData.stopLossTrailings.map((initialStopLossTrailing) => ({
        ...initialStopLossTrailing,
        id: newModuleId ? Date.now() + Math.random() * 1000 : initialStopLossTrailing.id || Date.now() + Math.random() * 1000,
        name: initialStopLossTrailing.name || "Unnamed stop loss trailing",
        config: initialStopLossTrailing.config || null,
      }));
    }
    setStopLossTrailings(initialStopLossTrailings);

    let initialInitialTakeProfit = {};
    if (moduleData && moduleData.initialTakeProfit) {
      initialInitialTakeProfit = moduleData.initialTakeProfit;
    } else {
      initialInitialTakeProfit = InitialTakeProfitDefaults;
    }
    setInitialTakeProfit(initialInitialTakeProfit);

    let initialTakeProfitDynamics = [];
    if (moduleData && moduleData.takeProfitDynamics && Array.isArray(moduleData.takeProfitDynamics)) {
      initialTakeProfitDynamics = moduleData.takeProfitDynamics.map((initialTakeProfitDynamic) => ({
        ...initialTakeProfitDynamic,
        id: newModuleId ? Date.now() + Math.random() * 1000 : initialTakeProfitDynamic.id || Date.now() + Math.random() * 1000,
        name: initialTakeProfitDynamic.name || "Unnamed take profit dynamic",
        config: initialTakeProfitDynamic.config || null,
      }));
    }
    setTakeProfitDynamics(initialTakeProfitDynamics);

    let initialSymbolAndTimeframe = {};
    if (moduleData && moduleData.symbolAndTimeframe) {
      initialSymbolAndTimeframe = moduleData.symbolAndTimeframe;
    } else {
      initialSymbolAndTimeframe = SymbolAndTimeframeDefaults;
    }
    setSymbolAndTimeframe(initialSymbolAndTimeframe);

    let initialVolumeCalculation = {};
    if (moduleData && moduleData.volumeCalculation) {
      initialVolumeCalculation = moduleData.volumeCalculation;
    } else {
      initialVolumeCalculation = VolumeCalculationDefaults;
    }
    setVolumeCalculation(initialVolumeCalculation);

    let initialPendingOrderSettings = {};
    if (moduleData && moduleData.pendingOrderSettings) {
      initialPendingOrderSettings = moduleData.pendingOrderSettings;
    } else {
      initialPendingOrderSettings = PendingOrderSettingsDefaults;
    }
    setPendingOrderSettings(initialPendingOrderSettings);

    let initialTradeSplittings = [];
    if (moduleData && moduleData.tradeSplittings && Array.isArray(moduleData.tradeSplittings)) {
      initialTradeSplittings = moduleData.tradeSplittings.map((initialTradeSplitting) => ({
        ...initialTradeSplitting,
        id: newModuleId ? Date.now() + Math.random() * 1000 : initialTradeSplitting.id || Date.now() + Math.random() * 1000,
        name: initialTradeSplitting.name || "Unnamed trade splittings",
        config: initialTradeSplitting.config || null,
      }));
    }
    setTradeSplittings(initialTradeSplittings);

    let initialTiming = {};
    if (moduleData && moduleData.timing) {
      initialTiming = moduleData.timing;
    } else {
      initialTiming = TimingDefaults;
    }
    setTiming(initialTiming);

    let initialMonitoring = {};
    if (moduleData && moduleData.monitoring) {
      initialMonitoring = moduleData.monitoring;
    } else {
      initialMonitoring = MonitoringDefaults;
    }
    setMonitoring(initialMonitoring);

    let initialDataFeedback = {};
    if (moduleData && moduleData.dataFeedback) {
      initialDataFeedback = moduleData.dataFeedback;
    } else {
      initialDataFeedback = DataFeedbackDefaults;
    }
    setDataFeedback(initialDataFeedback);

    let initialMiscellaneous = {};
    if (moduleData && moduleData.miscellaneous) {
      initialMiscellaneous = moduleData.miscellaneous;
    } else {
      initialMiscellaneous = MiscellaneousDefaults;
    }
    setMiscellaneous(initialMiscellaneous);

    let initialNotifications = {};
    if (moduleData && moduleData.notifications) {
      initialNotifications = moduleData.notifications;
    } else {
      initialNotifications = NotificationsDefaults;
    }
    setNotifications(initialNotifications);

    let initialIndicators = [];
    if (moduleData && moduleData.indicators && Array.isArray(moduleData.indicators)) {
      initialIndicators = moduleData.indicators.map((indicator) => ({
        ...indicator,
        id: newModuleId ? moduleId + Math.random() * 1000 : indicator.id || moduleId + Math.random() * 1000,
        name: indicator.name || "Unnamed indicator",
        config: indicator.config || null,
      }));
    }
    setIndicators(initialIndicators);

    let initialIndicatorCombinations = [];
    if (moduleData && moduleData.indicatorCombinations && Array.isArray(moduleData.indicatorCombinations)) {
      initialIndicatorCombinations = moduleData.indicatorCombinations.map((indicatorCombination) => ({
        ...indicatorCombination,
        id: newModuleId ? Date.now() + Math.random() * 1000 : indicatorCombination.id || Date.now() + Math.random() * 1000,
        name: indicatorCombination.name || "Unnamed indicator combination",
        config: indicatorCombination.config || null,
      }));
    }
    setIndicatorCombinations(initialIndicatorCombinations);

    let initialChartAnalyzers = [];
    if (moduleData && moduleData.chartAnalyzers && Array.isArray(moduleData.chartAnalyzers)) {
      initialChartAnalyzers = moduleData.chartAnalyzers.map((initialChartAnalyzer) => ({
        ...initialChartAnalyzer,
        id: newModuleId ? Date.now() + Math.random() * 1000 : initialChartAnalyzer.id || Date.now() + Math.random() * 1000,
        name: initialChartAnalyzer.name || "Unnamed chart analyzer",
        config: initialChartAnalyzer.config || null,
      }));
    }
    setChartAnalyzers(initialChartAnalyzers);

    let initialNewsConfig = {};
    if (moduleData && moduleData.news) {
      initialNewsConfig = moduleData.news;
    } else {
      initialNewsConfig = NewsDefaults;
    }
    setNews(initialNewsConfig);

    let initialCandlePatternsConfig = {};
    if (moduleData && moduleData.candlePatterns) {
      initialCandlePatternsConfig = moduleData.candlePatterns;
    } else {
      initialCandlePatternsConfig = CandlePatternsDefaults;
    }
    setCandlePatterns(initialCandlePatternsConfig);

    let initialChartPatternsConfig = {};
    if (moduleData && moduleData.chartPatterns) {
      initialChartPatternsConfig = moduleData.chartPatterns;
    } else {
      initialChartPatternsConfig = ChartPatternsDefaults;
    }
    setChartPatterns(initialChartPatternsConfig);
    let initialFibonacciPatternsConfig = {};
    if (moduleData && moduleData.fibonacciPatterns) {
      initialFibonacciPatternsConfig = moduleData.fibonacciPatterns;
    } else {
      initialFibonacciPatternsConfig = FibonacciPatternsDefaults;
    }
    setFibonacciPatterns(initialFibonacciPatternsConfig);

    // Determine module flags
    const isImported = !!(moduleData && !newModuleId && !newModuleNumber);
    const isExample = !!(moduleData && newModuleId && newModuleNumber);

    // Add the new module to the modules array
    setModules((prev) => [
      ...prev,
      {
        id: moduleId,
        name: moduleName,
        alreadyOpened: true, // Mark as already opened since we're selecting it
        isExample,
        isImported,
        moduleData: moduleData || null, // Store the original data
      },
    ]);

    // Mark that user has added a module
    setHasEverAddedModule(true);

    // Always hide onboarding step 1 when a module is added
    setShowOnboardingStep1(false);
    setShouldShowMessage(false);

    // For first-time users, show step 2 after a short delay
    if (isFirstTimeUser && !hasEverAddedModule) {
      setTimeout(() => {
        if (moduleData) {
          setShowOnboardingStep2(false);
        } else {
          setShowOnboardingStep2(true);
        }
      }, 100);
    } else {
      setShowOnboardingStep2(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const addStopLossTrailing = () => {
    // Add limit check at the beginning
    if (stopLossTrailings.length >= LIMITS.MAX_STOP_LOSS_TRAILINGS) {
      return; // Or show an error message
    }

    const newStopLossTrailingId = Date.now();

    // Create the new stop loss trailing with a flag and config property
    const newStopLossTrailing = {
      id: newStopLossTrailingId,
      name: `Unspecified stop loss trailing`,
      subtitle: `Please configure`,
      isNewlyCreated: true,
      config: null,
    };

    // Add the stop loss trailing to the list
    setStopLossTrailings((prev) => [...prev, newStopLossTrailing]);

    // Automatically select the new stop loss trailing
    setSelectedStopLossTrailing(newStopLossTrailingId);

    // Hide onboarding step 2
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const addTakeProfitDynamic = () => {
    // Add limit check at the beginning
    if (takeProfitDynamics.length >= LIMITS.MAX_TAKE_PROFIT_DYNAMICS) {
      return; // Or show an error message
    }

    const newTakeProfitDynamicId = Date.now();

    // Create the new take profit dynamic with a flag and config property
    const newTakeProfitDynamic = {
      id: newTakeProfitDynamicId,
      name: `Unspecified dynamic take profit`,
      subtitle: `Please configure`,
      isNewlyCreated: true,
      config: null,
    };

    // Add the take profit dynamic to the list
    setTakeProfitDynamics((prev) => [...prev, newTakeProfitDynamic]);

    // Automatically select the new take profit dynamic
    setSelectedTakeProfitDynamic(newTakeProfitDynamicId);

    // Hide onboarding step 2
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const addTradeSplitting = () => {
    // Add limit check at the beginning
    if (tradeSplittings.length >= LIMITS.MAX_TRADE_SPLITTINGS) {
      return; // Or show an error message
    }

    const newTradeSplittingId = Date.now();

    // Create the new trade splitting with a flag and config property
    const newTradeSplitting = {
      id: newTradeSplittingId,
      name: `Unspecified trade splitting`,
      subtitle: `Please configure`,
      isNewlyCreated: true,
      config: null,
    };

    // Add the trade splitting to the list
    setTradeSplittings((prev) => [...prev, newTradeSplitting]);

    // Automatically select the new trade splitting
    setSelectedTradeSplitting(newTradeSplittingId);

    // Hide onboarding step 2
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const addIndicator = () => {
    // Add limit check at the beginning
    if (indicators.length >= LIMITS.MAX_INDICATORS) {
      return; // Or show an error message
    }

    const newIndicatorId = Date.now();

    // Create the new indicator with a flag and config property
    const newIndicator = {
      id: newIndicatorId,
      name: `Unspecified indicator`,
      subtitle: `Please configure`,
      isNewlyCreated: true,
      config: null,
    };

    // Add the indicator to the list
    setIndicators((prev) => [...prev, newIndicator]);

    // Automatically select the new indicator
    setSelectedIndicator(newIndicatorId);

    // Hide onboarding step 2
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const addIndicatorCombination = () => {
    // Add limit check at the beginning
    if (indicatorCombinations.length >= LIMITS.MAX_INDICATOR_COMBINATIONS) {
      return; // Or show an error message
    }

    const newIndicatorCombinationId = Date.now();

    // Create the new indicatorCombination with a flag and config property
    const newIndicatorCombination = {
      id: newIndicatorCombinationId,
      name: `Unspecified indicator combination`,
      subtitle: `Please configure`,
      isNewlyCreated: true,
      config: null,
    };

    // Add the indicator combination to the list
    setIndicatorCombinations((prev) => [...prev, newIndicatorCombination]);

    // Automatically select the new indicator combination
    setSelectedIndicatorCombination(newIndicatorCombinationId);

    // Hide onboarding step 2
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const addChartAnalyzer = () => {
    // Add limit check at the beginning
    if (chartAnalyzers.length >= LIMITS.MAX_CHART_ANALYZERS) {
      return; // Or show an error message
    }

    const newChartAnalyzerId = Date.now();

    // Create the new chart analyzer with a flag and config property
    const newChartAnalyzer = {
      id: newChartAnalyzerId,
      name: `Unspecified chart analyzer`,
      subtitle: `Please configure`,
      isNewlyCreated: true,
      config: null,
    };

    // Add the chart analyzer to the list
    setChartAnalyzers((prev) => [...prev, newChartAnalyzer]);

    // Automatically select the new chart analyzer
    setSelectedChartAnalyzer(newChartAnalyzerId);

    // Hide onboarding step 2
    if (showOnboardingStep2 === true) {
      setShowOnboardingStep2(false);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const triggerStopLossTrailingConfigDialog = () => {
    if (stopLossTrailingConfigureRef.current) {
      stopLossTrailingConfigureRef.current.openDialog();
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!selectedStopLossTrailing) return;

    // Find the selected stop loss trailing
    const selectedStopLossTrailingData = stopLossTrailings.find((ind) => ind.id === selectedStopLossTrailing);

    if (selectedStopLossTrailingData?.isNewlyCreated) {
      // Remove the flag first
      setStopLossTrailings((prev) => prev.map((ind) => (ind.id === selectedStopLossTrailing ? { ...ind, isNewlyCreated: false } : ind)));

      // Then trigger the dialog
      setTimeout(() => {
        triggerStopLossTrailingConfigDialog(selectedStopLossTrailing);
      }, 100);
    }
  }, [selectedStopLossTrailing]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const triggerTakeProfitDynamicConfigDialog = () => {
    if (takeProfitDynamicConfigureRef.current) {
      takeProfitDynamicConfigureRef.current.openDialog();
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!selectedTakeProfitDynamic) return;

    // Find the selected takeprofit dynamic
    const selectedTakeProfitDynamicData = takeProfitDynamics.find((ind) => ind.id === selectedTakeProfitDynamic);

    if (selectedTakeProfitDynamicData?.isNewlyCreated) {
      // Remove the flag first
      setTakeProfitDynamics((prev) => prev.map((ind) => (ind.id === selectedTakeProfitDynamic ? { ...ind, isNewlyCreated: false } : ind)));

      // Then trigger the dialog
      setTimeout(() => {
        triggerTakeProfitDynamicConfigDialog(selectedTakeProfitDynamic);
      }, 100);
    }
  }, [selectedTakeProfitDynamic]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const triggerTradeSplittingConfigDialog = () => {
    if (tradeSplittingConfigureRef.current) {
      tradeSplittingConfigureRef.current.openDialog();
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!selectedTradeSplitting) return;

    // Find the selected tradeSplitting
    const selectedTradeSplittingData = tradeSplittings.find((ind) => ind.id === selectedTradeSplitting);

    if (selectedTradeSplittingData?.isNewlyCreated) {
      // Remove the flag first
      setTradeSplittings((prev) => prev.map((ind) => (ind.id === selectedTradeSplitting ? { ...ind, isNewlyCreated: false } : ind)));

      // Then trigger the dialog
      setTimeout(() => {
        triggerTradeSplittingConfigDialog(selectedTradeSplitting);
      }, 100);
    }
  }, [selectedTradeSplitting]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const triggerIndicatorConfigDialog = () => {
    if (indicatorConfigureRef.current) {
      indicatorConfigureRef.current.openDialog();
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!selectedIndicator) return;

    // Find the selected indicator
    const selectedIndicatorData = indicators.find((ind) => ind.id === selectedIndicator);

    if (selectedIndicatorData?.isNewlyCreated) {
      // Remove the flag first
      setIndicators((prev) => prev.map((ind) => (ind.id === selectedIndicator ? { ...ind, isNewlyCreated: false } : ind)));

      // Then trigger the dialog
      setTimeout(() => {
        triggerIndicatorConfigDialog(selectedIndicator);
      }, 100);
    }
  }, [selectedIndicator]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const triggerIndicatorCombinationConfigDialog = () => {
    if (indicatorCombinationConfigureRef.current) {
      indicatorCombinationConfigureRef.current.openDialog();
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!selectedIndicatorCombination) return;

    // Find the selected indicator combination
    const selectedIndicatorCombinationData = indicatorCombinations.find((ind) => ind.id === selectedIndicatorCombination);

    if (selectedIndicatorCombinationData?.isNewlyCreated) {
      // Remove the flag first
      setIndicatorCombinations((prev) => prev.map((ind) => (ind.id === selectedIndicatorCombination ? { ...ind, isNewlyCreated: false } : ind)));

      // Then trigger the dialog
      setTimeout(() => {
        triggerIndicatorCombinationConfigDialog(selectedIndicatorCombination);
      }, 100);
    }
  }, [selectedIndicatorCombination]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const triggerChartAnalyzerConfigDialog = () => {
    if (chartAnalyzerConfigureRef.current) {
      chartAnalyzerConfigureRef.current.openDialog();
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!selectedChartAnalyzer) return;

    // Find the selected chart analyzer
    const selectedChartAnalyzerData = chartAnalyzers.find((ind) => ind.id === selectedChartAnalyzer);

    if (selectedChartAnalyzerData?.isNewlyCreated) {
      // Remove the flag first
      setChartAnalyzers((prev) => prev.map((ind) => (ind.id === selectedChartAnalyzer ? { ...ind, isNewlyCreated: false } : ind)));

      // Then trigger the dialog
      setTimeout(() => {
        triggerChartAnalyzerConfigDialog(selectedChartAnalyzer);
      }, 100);
    }
  }, [selectedChartAnalyzer]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const removeSpecificModule = (identifier) => {
    const item = modules.find((item) => item.id === identifier);

    setConfirmationDialog({
      open: true,
      title: "Remove trading module",
      message: (
        <>
          Are you sure you want to remove <b>{item?.name || "this item"}</b>?<br />
          This step can not be undone!
        </>
      ),
      onConfirm: () => {
        // Always hide onboarding messages
        setShouldShowMessage(false);
        setShowOnboardingStep2(false);
        setShowOnboardingStep2(false);
        setHasEverAddedModule(true);

        // Get the module data before removing it
        const moduleToRemove = superDataStructure.find((m) => m.moduleId === identifier);

        // Remove the module from the modules array
        setModules((prev) => prev.filter((item) => item.id !== identifier));

        // Clean up saved states for this module
        const moduleKey = getModuleKey(identifier);
        setModuleStates((prev) => {
          const newStates = { ...prev };
          delete newStates[moduleKey];
          return newStates;
        });

        // Clean up component states for this module
        setComponentStates((prev) => {
          const newStates = { ...prev };
          Object.keys(newStates).forEach((key) => {
            if (key.startsWith(`${identifier}_`)) {
              delete newStates[key];
            }
          });
          return newStates;
        });

        // Clean up tier3 states for this module
        setTier3States((prev) => {
          const newStates = { ...prev };
          Object.keys(newStates).forEach((key) => {
            if (key.startsWith(`${identifier}_`)) {
              delete newStates[key];
            }
          });
          return newStates;
        });

        // If the removed module was the currently selected one, clear all selections
        if (identifier === selectedModule) {
          setSelectedModule(null);
          setSelectedComponent(null);
          setSelectedTier3(null);
          setSelectedTier4(null);
          setSelectedStopLossTrailing(null);
          setSelectedTakeProfitDynamic(null);
          setSelectedTradeSplitting(null);
          setSelectedIndicator(null);
          setSelectedIndicatorCombination(null);
          setSelectedChartAnalyzer(null);

          // Clear the arrays since we're removing the selected module
          setStopLossTrailings([]);
          setTakeProfitDynamics([]);
          setTradeSplittings([]);
          setIndicators([]);
          setIndicatorCombinations([]);
          setChartAnalyzers([]);
        } else if (selectedModule) {
          // If we're removing a non-selected module but have a selected module,
          // reload the selected module's data to ensure arrays are properly synchronized
          // This prevents stale data from mixing with other modules
          loadModuleBasics(selectedModule);
        }

        // Update super data structure - remove the module
        setSuperDataStructure((prev) => prev.filter((m) => m.moduleId !== identifier));

        // If no modules left, clear localStorage including GUI state
        if (modules.length === 1) {
          // Will be 0 after the removal
          localStorage.removeItem(STORAGE_KEYS.DATA);
          localStorage.removeItem(STORAGE_KEYS.GUI_STATE);
        }

        setConfirmationDialog({ open: false, title: "", message: "", onConfirm: null, disabled: false });
      },
      disabled: false,
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const removeSpecificStopLossTrailing = (identifier) => {
    const item = stopLossTrailings.find((item) => item.id === identifier);

    setConfirmationDialog({
      open: true,
      title: "Remove stop loss trailing",
      message: (
        <>
          Are you sure you want to remove <b>{item?.name || "this item"}</b>?<br />
          This step can not be undone!
        </>
      ),
      onConfirm: () => {
        setStopLossTrailings((prev) => prev.filter((item) => item.id !== identifier));
        if (identifier === selectedStopLossTrailing) {
          setSelectedStopLossTrailing(null);
        }
        setConfirmationDialog({ open: false, title: "", message: "", onConfirm: null, disabled: false });
      },
      disabled: false,
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const removeSpecificTakeProfitDynamic = (identifier) => {
    const item = takeProfitDynamics.find((item) => item.id === identifier);

    setConfirmationDialog({
      open: true,
      title: "Remove dynamic take profit",
      message: (
        <>
          Are you sure you want to remove <b>{item?.name || "this item"}</b>?<br />
          This step can not be undone!
        </>
      ),
      onConfirm: () => {
        setTakeProfitDynamics((prev) => prev.filter((item) => item.id !== identifier));
        if (identifier === selectedTakeProfitDynamic) {
          setSelectedTakeProfitDynamic(null);
        }

        setConfirmationDialog({ open: false, title: "", message: "", onConfirm: null, disabled: false });
      },
      disabled: false,
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const removeSpecificTradeSplitting = (identifier) => {
    const item = tradeSplittings.find((item) => item.id === identifier);

    setConfirmationDialog({
      open: true,
      title: "Remove trade splitting",
      message: (
        <>
          Are you sure you want to remove <b>{item?.name || "this item"}</b>?<br />
          This step can not be undone!
        </>
      ),
      onConfirm: () => {
        setTradeSplittings((prev) => prev.filter((item) => item.id !== identifier));
        if (identifier === selectedTradeSplitting) {
          setSelectedTradeSplitting(null);
        }

        setConfirmationDialog({ open: false, title: "", message: "", onConfirm: null, disabled: false });
      },
      disabled: false,
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const removeSpecificIndicator = (identifier) => {
    const item = indicators.find((item) => item.id === identifier);

    // Check for dependecies
    const dependencies = [];

    if (pendingOrderSettings.nPrice === "2" && parseInt(pendingOrderSettings.nInosUsedForValueInjection, 10) === item.id) {
      dependencies.push("Pending order settings");
    }

    if (initialStopLoss.nSelection === "3" && parseInt(initialStopLoss.nInosUsedForValueInjection, 10) === item.id) {
      dependencies.push("Initial stop loss");
    }

    stopLossTrailings.forEach((stopLossTrailing) => {
      if (stopLossTrailing.config !== undefined && stopLossTrailing.config !== null) {
        if (stopLossTrailing.config.nType === "4") {
          if (parseInt(stopLossTrailing.config.nInosUsedForValueInjection, 10) === item.id) {
            dependencies.push(`Stop loss trailing -${stopLossTrailing.name} - ${stopLossTrailing.subtitle}`);
          }
        }
      }
    });

    if (initialTakeProfit.nSelection === "4" && parseInt(initialTakeProfit.nInosUsedForValueInjection, 10) === item.id) {
      dependencies.push("Initial take profit");
    }

    takeProfitDynamics.forEach((takeProfitDynamic) => {
      if (takeProfitDynamic.config !== undefined && takeProfitDynamic.config !== null) {
        if (takeProfitDynamic.config.nType === "4") {
          if (parseInt(takeProfitDynamic.config.nInosUsedForValueInjection, 10) === item.id) {
            dependencies.push(`Dynamic take profit -${takeProfitDynamic.name} - ${takeProfitDynamic.subtitle}`);
          }
        }
      }
    });

    indicatorCombinations.forEach((indicatorCombination) => {
      if (indicatorCombination.config !== undefined && indicatorCombination.config !== null) {
        if (parseInt(indicatorCombination.config.nInputInos1, 10) === item.id) {
          dependencies.push(`Indicator Combination - ${indicatorCombination.name} - ${indicatorCombination.subtitle}`);
        }
        if (parseInt(indicatorCombination.config.nInputInos2, 10) === item.id) {
          dependencies.push(`Indicator Combination - ${indicatorCombination.name} - ${indicatorCombination.subtitle}`);
        }
      }
    });

    if (volumeCalculation.nVolumeCalculation === "3" && parseInt(volumeCalculation.nInosUsedForValueInjection, 10) === item.id) {
      dependencies.push("Volume calculation");
    }

    if (dependencies.length > 0) {
      setConfirmationDialog({
        open: true,
        title: "Indicator dependencies identified",
        message: (
          <>
            The object has dependencies on the components listed below. To ensure safe removal, please verify and address these dependencies before
            proceeding.
            <br />
            <ul>
              {dependencies.map((dependency, index) => (
                <li key={index}>{dependency}</li>
              ))}
            </ul>
          </>
        ),
        onConfirm: () => {
          setConfirmationDialog({ open: false, title: "", message: "", onConfirm: null, disabled: false });
        },
        disabled: true,
      });
    } else {
      setConfirmationDialog({
        open: true,
        title: "Remove indicator",
        message: (
          <>
            Are you sure you want to remove <b>{item?.name || "this item"}</b>?<br />
            This step can not be undone!
          </>
        ),
        onConfirm: () => {
          setIndicators((prev) => prev.filter((item) => item.id !== identifier));
          if (identifier === selectedIndicator) {
            setSelectedIndicator(null);
          }

          setConfirmationDialog({ open: false, title: "", message: "", onConfirm: null, disabled: false });

          // Clear from hidden values as well
          if (parseInt(pendingOrderSettings.nInosUsedForValueInjection, 10) === item.id) {
            setPendingOrderSettings((prev) => ({
              ...prev,
              nInosUsedForValueInjection: "0",
            }));
          }

          if (parseInt(initialStopLoss.nInosUsedForValueInjection, 10) === item.id) {
            setInitialStopLoss((prev) => ({
              ...prev,
              nInosUsedForValueInjection: "0",
            }));
          }

          stopLossTrailings.forEach((stopLossTrailing) => {
            if (stopLossTrailing.config !== undefined && stopLossTrailing.config !== null) {
              if (parseInt(stopLossTrailing.config.nInosUsedForValueInjection, 10) === item.id) {
                setStopLossTrailings((prev) =>
                  prev.map((slt) => (slt.id === stopLossTrailing.id ? { ...slt, config: { ...slt.config, nInosUsedForValueInjection: "0" } } : slt))
                );
              }
            }
          });

          if (parseInt(initialTakeProfit.nInosUsedForValueInjection, 10) === item.id) {
            setInitialTakeProfit((prev) => ({
              ...prev,
              nInosUsedForValueInjection: "0",
            }));
          }

          takeProfitDynamics.forEach((takeProfitDynamic) => {
            if (takeProfitDynamic.config !== undefined && takeProfitDynamic.config !== null) {
              if (parseInt(takeProfitDynamic.config.nInosUsedForValueInjection, 10) === item.id) {
                setTakeProfitDynamics((prev) =>
                  prev.map((tpd) => (tpd.id === takeProfitDynamic.id ? { ...tpd, config: { ...tpd.config, nInosUsedForValueInjection: "0" } } : tpd))
                );
              }
            }
          });

          if (parseInt(volumeCalculation.nInosUsedForValueInjection, 10) === item.id) {
            setVolumeCalculation((prev) => ({
              ...prev,
              nInosUsedForValueInjection: "0",
            }));
          }
        },
        disabled: false,
      });
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const removeSpecificIndicatorCombination = (identifier) => {
    const item = indicatorCombinations.find((item) => item.id === identifier);

    setConfirmationDialog({
      open: true,
      title: "Remove indicator combination",
      message: (
        <>
          Are you sure you want to remove <b>{item?.name || "this item"}</b>?<br />
          This step can not be undone!
        </>
      ),
      onConfirm: () => {
        setIndicatorCombinations((prev) => prev.filter((item) => item.id !== identifier));
        if (identifier === selectedIndicatorCombination) {
          setSelectedIndicatorCombination(null);
        }

        setConfirmationDialog({ open: false, title: "", message: "", onConfirm: null, disabled: false });
      },
      disabled: false,
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const removeSpecificChartAnalyzer = (identifier) => {
    const item = chartAnalyzers.find((item) => item.id === identifier);

    // Check for dependecies
    const dependencies = [];

    if (pendingOrderSettings.nPrice === "3" && parseInt(pendingOrderSettings.nCaSupResUsedForValueInjection, 10) === item.id) {
      dependencies.push("Pending order settings");
    }

    if (initialStopLoss.nSelection === "4" && parseInt(initialStopLoss.nCaSupResUsedForValueInjection, 10) === item.id) {
      dependencies.push("Initial stop loss");
    }

    stopLossTrailings.forEach((stopLossTrailing) => {
      if (stopLossTrailing.config !== undefined && stopLossTrailing.config !== null) {
        if (stopLossTrailing.config.nType === "5") {
          if (parseInt(stopLossTrailing.config.nCaSupResUsedForValueInjection, 10) === item.id) {
            dependencies.push(`Stop loss trailing -${stopLossTrailing.name} - ${stopLossTrailing.subtitle}`);
          }
        }
      }
    });

    if (initialTakeProfit.nSelection === "5" && parseInt(initialTakeProfit.nCaSupResUsedForValueInjection, 10) === item.id) {
      dependencies.push("Initial take profit");
    }

    takeProfitDynamics.forEach((takeProfitDynamic) => {
      if (takeProfitDynamic.config !== undefined && takeProfitDynamic.config !== null) {
        if (takeProfitDynamic.config.nType === "5") {
          if (parseInt(takeProfitDynamic.config.nCaSupResUsedForValueInjection, 10) === item.id) {
            dependencies.push(`Dynamic take profit -${takeProfitDynamic.name} - ${takeProfitDynamic.subtitle}`);
          }
        }
      }
    });

    if (dependencies.length > 0) {
      setConfirmationDialog({
        open: true,
        title: "Chart analyzer dependencies identified",
        message: (
          <>
            The object has dependencies on the components listed below. To ensure safe removal, please verify and address these dependencies before
            proceeding.
            <br />
            <ul>
              {dependencies.map((dependency, index) => (
                <li key={index}>{dependency}</li>
              ))}
            </ul>
          </>
        ),
        onConfirm: () => {
          setConfirmationDialog({ open: false, title: "", message: "", onConfirm: null, disabled: false });
        },
        disabled: true,
      });
    } else {
      setConfirmationDialog({
        open: true,
        title: "Remove chart analyzer",
        message: (
          <>
            Are you sure you want to remove <b>{item?.name || "this item"}</b>?<br />
            This step can not be undone!
          </>
        ),
        onConfirm: () => {
          setChartAnalyzers((prev) => prev.filter((item) => item.id !== identifier));
          if (identifier === selectedChartAnalyzer) {
            setSelectedChartAnalyzer(null);
          }

          setConfirmationDialog({ open: false, title: "", message: "", onConfirm: null, disabled: false });

          // Clear from hidden values as well
          if (parseInt(pendingOrderSettings.nCaSupResUsedForValueInjection, 10) === item.id) {
            setPendingOrderSettings((prev) => ({
              ...prev,
              nCaSupResUsedForValueInjection: "0",
            }));
          }

          if (parseInt(initialStopLoss.nCaSupResUsedForValueInjection, 10) === item.id) {
            setInitialStopLoss((prev) => ({
              ...prev,
              nCaSupResUsedForValueInjection: "0",
            }));
          }

          stopLossTrailings.forEach((stopLossTrailing) => {
            if (stopLossTrailing.config !== undefined && stopLossTrailing.config !== null) {
              if (parseInt(stopLossTrailing.config.nCaSupResUsedForValueInjection, 10) === item.id) {
                setStopLossTrailings((prev) =>
                  prev.map((slt) =>
                    slt.id === stopLossTrailing.id ? { ...slt, config: { ...slt.config, nCaSupResUsedForValueInjection: "0" } } : slt
                  )
                );
              }
            }
          });

          if (parseInt(initialTakeProfit.nCaSupResUsedForValueInjection, 10) === item.id) {
            setInitialTakeProfit((prev) => ({
              ...prev,
              nCaSupResUsedForValueInjection: "0",
            }));
          }

          takeProfitDynamics.forEach((takeProfitDynamic) => {
            if (takeProfitDynamic.config !== undefined && takeProfitDynamic.config !== null) {
              if (parseInt(takeProfitDynamic.config.nCaSupResUsedForValueInjection, 10) === item.id) {
                setTakeProfitDynamics((prev) =>
                  prev.map((tpd) =>
                    tpd.id === takeProfitDynamic.id ? { ...tpd, config: { ...tpd.config, nCaSupResUsedForValueInjection: "0" } } : tpd
                  )
                );
              }
            }
          });
        },
        disabled: false,
      });
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (modules.length === 0) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (modules.length === 0 && shouldShowMessage) {
      const interval = setInterval(() => {
        setIsVisible(false);
        setTimeout(() => {
          setCurrentMessageIndex((prev) => (prev + 1) % MARKETING_MESSAGES.length);
          setMessagePosition(generateRandomPosition());
          setIsVisible(true);
        }, 500);
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [MARKETING_MESSAGES.length, modules.length, shouldShowMessage]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (modules.length > 0) {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldShowMessage(false);
      }, 500);
      return () => clearTimeout(timer);
    }
    setShouldShowMessage(true);
    setTimeout(() => {
      setMessagePosition(generateRandomPosition());
      setIsVisible(true);
    }, 100);
  }, [modules.length]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const selectedModuleName = modules.find((module) => module.id === selectedModule);
  //
  const selectedStopLossTrailingName = stopLossTrailings.find((stopLossTrailing) => stopLossTrailing.id === selectedStopLossTrailing);
  const selectedTakeProfitDynamicName = takeProfitDynamics.find((takeProfitDynamic) => takeProfitDynamic.id === selectedTakeProfitDynamic);
  const selectedTradeSplittingName = tradeSplittings.find((tradeSplitting) => tradeSplitting.id === selectedTradeSplitting);
  //
  const selectedIndicatorName = indicators.find((indicator) => indicator.id === selectedIndicator);
  const selectedIndicatorCombinationName = indicatorCombinations.find(
    (indicatorCombination) => indicatorCombination.id === selectedIndicatorCombination
  );
  const selectedChartAnalyzerName = chartAnalyzers.find((chartAnalyzer) => chartAnalyzer.id === selectedChartAnalyzer);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const saveToLocalStorage = useCallback((data) => {
    if (!isLoadingFromStorage.current) {
      try {
        localStorage.setItem(STORAGE_KEYS.DATA, JSON.stringify(data));
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
    }
  }, []);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const saveGUIStateToLocalStorage = useCallback((guiState) => {
    if (!isLoadingFromStorage.current) {
      try {
        localStorage.setItem(STORAGE_KEYS.GUI_STATE, JSON.stringify(guiState));
      } catch (error) {
        console.error("Error saving GUI state to localStorage:", error);
      }
    }
  }, []);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (selectedModule && state_strIdeaOutputDescription !== undefined) {
      setModules((prev) =>
        prev.map((module) => {
          if (module.id === selectedModule) {
            // Extract the module number from current name
            const match = module?.name?.match(/Module (\d+)/);
            const moduleNumber = match ? match[1] : "";

            // Create new name based on description (crop after 32 characters)
            const description = state_strIdeaOutputDescription;
            let trimmedDescription = "";
            if (description && description.trim() !== "") {
              trimmedDescription = description.trim();
              if (trimmedDescription.length > 32) {
                trimmedDescription = `${trimmedDescription.substring(0, 32)}...`;
              }
            }

            const newName = trimmedDescription !== "" ? `Module ${moduleNumber}: ${trimmedDescription}` : `Module ${moduleNumber}`;

            return {
              ...module,
              name: newName,
            };
          }
          return module;
        })
      );
    }
  }, [selectedModule, state_nTradingState, state_strIdeaOutputDescription]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    // Only save if we're not loading and the component has finished initial loading
    if (!isLoadingFromStorage.current && !loadingSpinner) {
      const guiState = {
        selectedModule,
        selectedComponent,
        selectedTier3,
        selectedTier4,
        selectedStopLossTrailing,
        selectedTakeProfitDynamic,
        selectedTradeSplitting,
        selectedIndicator,
        selectedIndicatorCombination,
        selectedChartAnalyzer,
        moduleStates,
        componentStates,
        tier3States,
        professionalMode,
        duplicateAction,
      };
      saveGUIStateToLocalStorage(guiState);
    }
  }, [
    selectedModule,
    selectedComponent,
    selectedTier3,
    selectedTier4,
    selectedStopLossTrailing,
    selectedTakeProfitDynamic,
    selectedTradeSplitting,
    selectedIndicator,
    selectedIndicatorCombination,
    selectedChartAnalyzer,
    moduleStates,
    componentStates,
    tier3States,
    professionalMode,
    duplicateAction,
    loadingSpinner,
    saveGUIStateToLocalStorage,
  ]);

  // Helper function to check if an object is empty or has only empty/undefined values
  const isEmptyStructure = (obj) => {
    if (!obj || typeof obj !== "object") return true;

    // Check if object is empty {}
    if (Object.keys(obj).length === 0) return true;

    // Check if all values are empty, null, undefined, or empty strings
    return Object.values(obj).every(
      (value) => value === null || value === undefined || value === "" || (typeof value === "object" && Object.keys(value).length === 0)
    );
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    // Validate that both objects have content before proceeding
    if (isEmptyStructure(tradeBasics) || isEmptyStructure(pendingOrderSettings)) {
      return; // Exit early if either structure is empty
    }

    if (professionalMode === true) {
      const newTradeBasics = {
        ...tradeBasics,
        nPrice: pendingOrderSettings.nPrice,
        dRateOfChangeActual: pendingOrderSettings.dRateOfChangeActual,
        nFixPipsActual: pendingOrderSettings.nFixPipsActual,
        nLimits: pendingOrderSettings.nLimits,
        nExpiration: pendingOrderSettings.nExpiration,
      };

      // Only update if there are actual changes to prevent infinite loops
      const hasChanges =
        tradeBasics.nPrice !== pendingOrderSettings.nPrice ||
        tradeBasics.dRateOfChangeActual !== pendingOrderSettings.dRateOfChangeActual ||
        tradeBasics.nFixPipsActual !== pendingOrderSettings.nFixPipsActual ||
        tradeBasics.nLimits !== pendingOrderSettings.nLimits ||
        tradeBasics.nExpiration !== pendingOrderSettings.nExpiration;

      // Only update if the new structure is not empty and there are changes
      if (hasChanges && !isEmptyStructure(newTradeBasics)) {
        setTradeBasics(newTradeBasics);
      }
    } else {
      const adjustedLimits = parseInt(tradeBasics.nLimits, 10) > 3 ? "3" : tradeBasics.nLimits;

      const newPendingOrderSettings = {
        ...pendingOrderSettings,
        nPrice: tradeBasics.nPrice,
        dRateOfChangeActual: tradeBasics.dRateOfChangeActual,
        nFixPipsActual: tradeBasics.nFixPipsActual,
        nLimits: adjustedLimits,
        nExpiration: tradeBasics.nExpiration,
      };

      // Only update if there are actual changes to prevent infinite loops
      const hasChanges =
        pendingOrderSettings.nPrice !== tradeBasics.nPrice ||
        pendingOrderSettings.dRateOfChangeActual !== tradeBasics.dRateOfChangeActual ||
        pendingOrderSettings.nFixPipsActual !== tradeBasics.nFixPipsActual ||
        pendingOrderSettings.nLimits !== adjustedLimits ||
        pendingOrderSettings.nExpiration !== tradeBasics.nExpiration;

      // Only update if the new structure is not empty and there are changes
      if (hasChanges && !isEmptyStructure(newPendingOrderSettings)) {
        setPendingOrderSettings(newPendingOrderSettings);
      }
    }
  }, [
    professionalMode,
    tradeBasics.nPrice,
    tradeBasics.dRateOfChangeActual,
    tradeBasics.nFixPipsActual,
    tradeBasics.nLimits,
    tradeBasics.nExpiration,
    pendingOrderSettings.nPrice,
    pendingOrderSettings.dRateOfChangeActual,
    pendingOrderSettings.nFixPipsActual,
    pendingOrderSettings.nLimits,
    pendingOrderSettings.nExpiration,
  ]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    // Validate that both objects have content before proceeding
    if (isEmptyStructure(tradeBasics) || isEmptyStructure(pendingOrderSettings)) {
      return; // Exit early if either structure is empty
    }

    if (professionalMode === true) {
      let nNewOperation = "";
      let nNewMode = "";
      switch (tradeBasics.nOrderType) {
        case "0": // buy
          nNewOperation = "0";
          nNewMode = "0";
          break;
        case "1": // buy limit
          nNewOperation = "0";
          nNewMode = "2";
          break;
        case "2": // buy stop
          nNewOperation = "0";
          nNewMode = "1";
          break;
        case "3": // sell
          nNewOperation = "2";
          nNewMode = "0";
          break;
        case "4": // sell limit
          nNewOperation = "2";
          nNewMode = "2";
          break;
        case "5": // sell stop
          nNewOperation = "2";
          nNewMode = "1";
          break;
        default:
          break;
      }

      const newTradeBasics = {
        ...tradeBasics,
        nOperation: nNewOperation,
      };

      const newPendingOrderSettings = {
        ...pendingOrderSettings,
        nMode: nNewMode,
      };

      // Only update if the new structures are not empty
      if (!isEmptyStructure(newTradeBasics)) {
        setTradeBasics(newTradeBasics);
      }
      if (!isEmptyStructure(newPendingOrderSettings)) {
        setPendingOrderSettings(newPendingOrderSettings);
      }
    } else {
      let nNewOrderType = "";

      // Determine nOrderType based on nOperation and nMode
      if (tradeBasics.nOperation === "0" || tradeBasics.nOperation === "2") {
        // buy operations
        switch (pendingOrderSettings.nMode) {
          case "0":
            nNewOrderType = "0"; // buy
            break;
          case "1":
            nNewOrderType = "2"; // buy stop
            break;
          case "2":
            nNewOrderType = "1"; // buy limit
            break;
          default:
            break;
        }
      } else if (tradeBasics.nOperation === "1" || tradeBasics.nOperation === "3") {
        // sell operations
        switch (pendingOrderSettings.nMode) {
          case "0":
            nNewOrderType = "3"; // sell
            break;
          case "1":
            nNewOrderType = "5"; // sell stop
            break;
          case "2":
            nNewOrderType = "4"; // sell limit
            break;
          default:
            break;
        }
      }

      const newTradeBasics = {
        ...tradeBasics,
        nOrderType: nNewOrderType,
      };

      // Only update if the new structure is not empty
      if (!isEmptyStructure(newTradeBasics)) {
        setTradeBasics(newTradeBasics);
      }
    }
  }, [professionalMode]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const loadFromLocalStorage = useCallback(() => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEYS.DATA);
      const savedGUIState = localStorage.getItem(STORAGE_KEYS.GUI_STATE);

      if (savedData) {
        // User has previous data - not a first-time user
        setIsFirstTimeUser(false);
        setHasEverAddedModule(true);
        isLoadingFromStorage.current = true;

        // Hide messages since previous state is restored
        setShowOnboardingStep1(false);
        setShowOnboardingStep2(false);
        setShouldShowMessage(false);

        const parsedData = JSON.parse(savedData);
        setSuperDataStructure(parsedData);
        setShowSessionRestoredMessage(true);

        // Load GUI state first if it exists
        if (savedGUIState) {
          const parsedGUIState = JSON.parse(savedGUIState);

          // Restore saved states first
          if (parsedGUIState.moduleStates) {
            setModuleStates(parsedGUIState.moduleStates);
          }
          if (parsedGUIState.componentStates) {
            setComponentStates(parsedGUIState.componentStates);
          }
          if (parsedGUIState.tier3States) {
            setTier3States(parsedGUIState.tier3States);
          }

          // Restore Professional Mode state immediately - with better fallback
          const savedProfessionalMode = parsedGUIState.professionalMode;
          if (savedProfessionalMode !== undefined && savedProfessionalMode !== null) {
            setProfessionalMode(savedProfessionalMode);
          }

          // Restore duplicateAction state immediately - with better fallback
          const savedDuplicateAction = parsedGUIState.duplicateAction;
          if (savedDuplicateAction !== undefined && savedDuplicateAction !== null) {
            setDuplicateAction(savedDuplicateAction);
          }
        }

        // Restore modules and their nested items
        if (parsedData.length > 0) {
          // Restore modules with proper names including descriptions
          const restoredModules = parsedData.map((moduleData) => {
            const moduleNumber = moduleData.basics?.nNumber || "";
            const description = moduleData.basics?.strIdeaOutputDescription || "";

            let moduleName;
            if (description && description.trim() !== "") {
              let trimmedDescription = description.trim();
              if (trimmedDescription.length > 32) {
                trimmedDescription = `${trimmedDescription.substring(0, 32)}...`;
              }
              moduleName = `Module ${moduleNumber}: ${trimmedDescription}`;
            } else {
              moduleName = `Module ${moduleNumber}`;
            }

            return {
              id: moduleData.moduleId,
              name: moduleName,
              alreadyOpened: true,
            };
          });
          setModules(restoredModules);

          // Initialize arrays to collect all nested items
          let allStopLossTrailings = [];
          let allTakeProfitDynamics = [];
          let allTradeSplittings = [];
          //
          let allIndicators = [];
          let allIndicatorCombinations = [];
          let allChartAnalyzers = [];

          // Restore nested items for each module
          parsedData.forEach((moduleData) => {
            if (moduleData.stopLossTrailings && Array.isArray(moduleData.stopLossTrailings)) {
              const processedStopLossTrailings = moduleData.stopLossTrailings.map((stopLossTrailing) => {
                // Check if config exists in the stop loss trailing itself
                if (stopLossTrailing.config) {
                  return stopLossTrailing;
                }

                // No config found, set to null
                return {
                  ...stopLossTrailing,
                  config: null,
                };
              });

              allStopLossTrailings = [...allStopLossTrailings, ...processedStopLossTrailings];
            }

            if (moduleData.takeProfitDynamics && Array.isArray(moduleData.takeProfitDynamics)) {
              const processedTakeProfitDynamics = moduleData.takeProfitDynamics.map((takeProfitDynamic) => {
                // Check if config exists in the take profit dynamic itself
                if (takeProfitDynamic.config) {
                  return takeProfitDynamic;
                }

                // No config found, set to null
                return {
                  ...takeProfitDynamic,
                  config: null,
                };
              });

              allTakeProfitDynamics = [...allTakeProfitDynamics, ...processedTakeProfitDynamics];
            }

            if (moduleData.tradeSplittings && Array.isArray(moduleData.tradeSplittings)) {
              const processedTradeSplittings = moduleData.tradeSplittings.map((tradeSplitting) => {
                // Check if config exists in the trade splitting itself
                if (tradeSplitting.config) {
                  return tradeSplitting;
                }

                // No config found, set to null
                return {
                  ...tradeSplitting,
                  config: null,
                };
              });

              allTradeSplittings = [...allTradeSplittings, ...processedTradeSplittings];
            }

            if (moduleData.indicators && Array.isArray(moduleData.indicators)) {
              const processedIndicators = moduleData.indicators.map((indicator) => {
                // Check if config exists in the indicator itself
                if (indicator.config) {
                  return indicator;
                }

                // No config found, set to null
                return {
                  ...indicator,
                  config: null,
                };
              });

              allIndicators = [...allIndicators, ...processedIndicators];
            }

            if (moduleData.indicatorCombinations && Array.isArray(moduleData.indicatorCombinations)) {
              const processedIndicatorCombinations = moduleData.indicatorCombinations.map((indicatorCombination) => {
                // Check if config exists in the indicator combination itself
                if (indicatorCombination.config) {
                  return indicatorCombination;
                }

                // No config found, set to null
                return {
                  ...indicatorCombination,
                  config: null,
                };
              });

              allIndicatorCombinations = [...allIndicatorCombinations, ...processedIndicatorCombinations];
            }

            if (moduleData.chartAnalyzers && Array.isArray(moduleData.chartAnalyzers)) {
              const processedChartAnalyzers = moduleData.chartAnalyzers.map((chartAnalyzer) => {
                // Check if config exists in the chart analyzer itself
                if (chartAnalyzer.config) {
                  return chartAnalyzer;
                }

                // No config found, set to null
                return {
                  ...chartAnalyzer,
                  config: null,
                };
              });

              allChartAnalyzers = [...allChartAnalyzers, ...processedChartAnalyzers];
            }
          });

          // Set data
          setStopLossTrailings(allStopLossTrailings);
          setTakeProfitDynamics(allTakeProfitDynamics);
          setTradeSplittings(allTradeSplittings);
          setIndicators([]);
          setIndicatorCombinations([]);
          setChartAnalyzers([]);

          // Restore GUI selections after data is loaded
          if (savedGUIState) {
            const parsedGUIState = JSON.parse(savedGUIState);

            // Use a longer timeout to ensure all data is set
            setTimeout(() => {
              setSelectedModule(parsedGUIState.selectedModule || null);
              setSelectedComponent(parsedGUIState.selectedComponent || null);
              setSelectedTier3(parsedGUIState.selectedTier3 || null);
              setSelectedTier4(parsedGUIState.selectedTier4 || null);
              //
              setSelectedStopLossTrailing(parsedGUIState.selectedStopLossTrailing || null);
              setSelectedTakeProfitDynamic(parsedGUIState.selectedTakeProfitDynamic || null);
              setSelectedTradeSplitting(parsedGUIState.selectedTradeSplitting || null);
              //
              setSelectedIndicator(parsedGUIState.selectedIndicator || null);
              setSelectedIndicatorCombination(parsedGUIState.selectedIndicatorCombination || null);
              setSelectedChartAnalyzer(parsedGUIState.selectedChartAnalyzer || null);

              // Load basics for the selected module if one was selected
              if (parsedGUIState.selectedModule) {
                loadModuleBasics(parsedGUIState.selectedModule);
              }

              // If a module is selected, restore its parameter states
              if (parsedGUIState.selectedModule) {
                const selectedModuleData = parsedData.find((moduleData) => moduleData.moduleId === parsedGUIState.selectedModule);

                if (selectedModuleData && selectedModuleData.basics) {
                  const params = selectedModuleData.basics;

                  // Restore Content states
                  if (params.nTradingState !== undefined) setState_nTradingState(params.nTradingState);
                  if (params.nNumber !== undefined) setState_nNumber(params.nNumber);
                  if (params.dVersion !== undefined) setState_dVersion(params.dVersion);
                  if (params.strIdeaOutputDescription !== undefined) setState_strIdeaOutputDescription(params.strIdeaOutputDescription);

                  // Reset formik with the loaded values
                  resetForm({
                    values: {
                      nTradingState: params.nTradingState || "1",
                      nNumber: params.nNumber || 1,
                      strIdeaOutputDescription: params.strIdeaOutputDescription || "",
                    },
                  });
                }

                // Restore trade basics config from module level
                if (selectedModuleData && selectedModuleData.tradeBasics !== undefined) {
                  setTradeBasics(selectedModuleData.tradeBasics);
                }

                // Restore initial stop loss config from module level
                if (selectedModuleData && selectedModuleData.initialStopLoss !== undefined) {
                  setInitialStopLoss(selectedModuleData.initialStopLoss);
                }

                // Restore initial take profit config from module level
                if (selectedModuleData && selectedModuleData.initialTakeProfit !== undefined) {
                  setInitialTakeProfit(selectedModuleData.initialTakeProfit);
                }

                // Restore symbol and timeframe config from module level
                if (selectedModuleData && selectedModuleData.symbolAndTimeframe !== undefined) {
                  setSymbolAndTimeframe(selectedModuleData.symbolAndTimeframe);
                }

                // Restore volume calculation config from module level
                if (selectedModuleData && selectedModuleData.volumeCalculation !== undefined) {
                  setVolumeCalculation(selectedModuleData.volumeCalculation);
                }

                // Restore pending order settings config from module level
                if (selectedModuleData && selectedModuleData.pendingOrderSettings !== undefined) {
                  setPendingOrderSettings(selectedModuleData.pendingOrderSettings);
                }

                // Restore timing config from module level
                if (selectedModuleData && selectedModuleData.timing !== undefined) {
                  setTiming(selectedModuleData.timing);
                }

                // Restore monitoring config from module level
                if (selectedModuleData && selectedModuleData.monitoring !== undefined) {
                  setMonitoring(selectedModuleData.monitoring);
                }

                // Restore data feedback config from module level
                if (selectedModuleData && selectedModuleData.dataFeedback !== undefined) {
                  setDataFeedback(selectedModuleData.dataFeedback);
                }

                // Restore miscellaneous config from module level
                if (selectedModuleData && selectedModuleData.miscellaneous !== undefined) {
                  setMiscellaneous(selectedModuleData.miscellaneous);
                }

                // Restore notifications config from module level
                if (selectedModuleData && selectedModuleData.notifications !== undefined) {
                  setNotifications(selectedModuleData.notifications);
                }

                // Restore trade basics config from module level
                if (selectedModuleData && selectedModuleData.news !== undefined) {
                  setNews(selectedModuleData.news);
                }

                // Restore trade basics config from module level
                if (selectedModuleData && selectedModuleData.candlePatterns !== undefined) {
                  setCandlePatterns(selectedModuleData.candlePatterns);
                }

                // Restore trade basics config from module level
                if (selectedModuleData && selectedModuleData.chartPatterns !== undefined) {
                  setChartPatterns(selectedModuleData.chartPatterns);
                }

                // Restore trade basics config from module level
                if (selectedModuleData && selectedModuleData.fibonacciPatterns !== undefined) {
                  setFibonacciPatterns(selectedModuleData.fibonacciPatterns);
                }
              }
            }, 200);
          }
        }

        // Use setTimeout to ensure all state updates have completed before allowing saves
        setTimeout(() => {
          isLoadingFromStorage.current = false;
        }, 300);
      } else {
        // No saved data - this is a first-time user
        setIsFirstTimeUser(true);
        setHasEverAddedModule(false);
      }
    } catch (error) {
      console.error("Error loading from localStorage:", error);
      isLoadingFromStorage.current = false;
      // On error, assume first-time user to be safe
      setIsFirstTimeUser(true);
      setHasEverAddedModule(false);
    }

    setLoadingSpinner(false);
  }, [loadModuleBasics]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    loadFromLocalStorage();
  }, []);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const updateModuleBasics = useCallback(() => {
    if (!isLoadingFromStorage.current) {
      setSuperDataStructure((prev) => {
        // Create a complete new structure for all modules
        const newData = modules.map((module) => {
          const moduleId = module.id;
          const existingModule = prev.find((m) => m.moduleId === moduleId);

          if (moduleId === selectedModule) {
            // Update the selected module with current state
            return {
              moduleId,
              moduleName: module.name || "",
              //
              basics: {
                nTradingState: state_nTradingState,
                nNumber: state_nNumber,
                dVersion: state_dVersion,
                strIdeaOutputDescription: state_strIdeaOutputDescription,
              },
              //-
              tradeBasics,
              initialStopLoss,
              stopLossTrailings,
              initialTakeProfit,
              takeProfitDynamics,
              symbolAndTimeframe,
              volumeCalculation,
              pendingOrderSettings,
              tradeSplittings,
              timing,
              monitoring,
              dataFeedback,
              miscellaneous,
              notifications,
              //-
              indicators,
              indicatorCombinations,
              chartAnalyzers,
              news,
              candlePatterns,
              chartPatterns,
              fibonacciPatterns,
            };
          }
          // Keep existing data for other modules or create default structure
          return (
            existingModule || {
              moduleId,
              moduleName: module.name || "",
              //
              basics: {
                nTradingState: "1",
                nNumber: 1,
                dVersion: "3.1",
                strIdeaOutputDescription: "",
              },
              //-
              tradeBasics: TradeBasicsDefaults,
              initialStopLoss: InitialStopLossDefaults,
              stopLossTrailings: [],
              initialTakeProfit: InitialTakeProfitDefaults,
              takeProfitDynamics: [],
              symbolAndTimeframe: SymbolAndTimeframeDefaults,
              volumeCalculation: VolumeCalculationDefaults,
              pendingOrderSettings: PendingOrderSettingsDefaults,
              tradeSplittings: [],
              timing: TimingDefaults,
              monitoring: MonitoringDefaults,
              dataFeedback: DataFeedbackDefaults,
              miscellaneous: MiscellaneousDefaults,
              notifications: NotificationsDefaults,
              //-
              indicators: [],
              indicatorCombinations: [],
              chartAnalyzers: [],
              news: NewsDefaults,
              candlePatterns: CandlePatternsDefaults,
              chartPatterns: ChartPatternsDefaults,
              fibonacciPatterns: FibonacciPatternsDefaults,
            }
          );
        });

        return newData;
      });
    }
  }, [
    modules,
    //
    state_nTradingState,
    state_nNumber,
    state_dVersion,
    state_strIdeaOutputDescription,
    //
    tradeBasics,
    initialStopLoss,
    stopLossTrailings,
    initialTakeProfit,
    takeProfitDynamics,
    symbolAndTimeframe,
    volumeCalculation,
    pendingOrderSettings,
    tradeSplittings,
    timing,
    monitoring,
    dataFeedback,
    miscellaneous,
    notifications,
    //
    indicators,
    indicatorCombinations,
    chartAnalyzers,
    news,
    candlePatterns,
    chartPatterns,
    fibonacciPatterns,
    //
    selectedModule,
    selectedComponent,
    selectedTier3,
    selectedTier4,
    //
    selectedStopLossTrailing,
    selectedTakeProfitDynamic,
    selectedTradeSplitting,
    //
    selectedIndicator,
    selectedIndicatorCombination,
    selectedChartAnalyzer,
  ]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    updateModuleBasics();
  }, [updateModuleBasics]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (superDataStructure.length > 0) {
      saveToLocalStorage(superDataStructure);
    }
  }, [superDataStructure, saveToLocalStorage]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  setTimeout(() => {
    setShowSessionRestoredMessage(false);
  }, 5000);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const findNextAvailableNumber = (modules, startFrom = 1, excludeModuleId = null, direction = "up") => {
    const usenNumbers = modules
      .filter((module) => module.id !== excludeModuleId) // Exclude current module from check
      .map((module) => {
        const match = module?.name?.match(/Module (\d+)/);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((num) => num !== null && num >= 1 && num <= 9999999999999);

    if (direction === "up") {
      // First, try to find available number starting from startFrom going up
      for (let num = startFrom; num <= 9999999999999; num += 1) {
        if (!usenNumbers.includes(num)) {
          return num;
        }
      }

      // If no number found from startFrom to 9999999999999, wrap around and search from 1
      for (let num = 1; num < startFrom; num += 1) {
        if (!usenNumbers.includes(num)) {
          return num;
        }
      }
    } else {
      // direction === 'down' - search downward first
      for (let num = startFrom; num >= 1; num -= 1) {
        if (!usenNumbers.includes(num)) {
          return num;
        }
      }

      // If no number found from startFrom to 1, wrap around and search from 9999999999999
      for (let num = 9999999999999; num > startFrom; num -= 1) {
        if (!usenNumbers.includes(num)) {
          return num;
        }
      }
    }

    return null; // No available numbers
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const isNumberUnique = (modules, number, excludeModuleId = null) => {
    const result = !modules.some((module) => module.id !== excludeModuleId);
    return result;
  }; 
 
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    // Check version on component mount
    const versionInfo = CheckSPAVersion();

    if (versionInfo.needsUpdate) {
      // console.log("Update completed successfully!");
    }

    if (versionInfo.isFirstTime) {
      // console.log("Welcome new user!");
      // Show onboarding or welcome message
    }
  }, []);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleCanvasDragEnter = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Check if any modal is open by looking for MUI modal backdrop or dialog elements
      const isModalOpen = document.querySelector('.MuiModal-backdrop, .MuiDialog-root, [role="dialog"]') !== null;

      // Only handle files if no modal is open
      if (!isModalOpen && e.dataTransfer.types.includes("Files")) {
        setCanvasDragCounter((prev) => prev + 1);
        if (!canvasDragActive) {
          setCanvasDragActive(true);
        }
      }
    },
    [canvasDragActive]
  );

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleCanvasDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if any modal is open
    const isModalOpen = document.querySelector('.MuiModal-backdrop, .MuiDialog-root, [role="dialog"]') !== null;

    // Only handle if no modal is open
    if (!isModalOpen) {
      setCanvasDragCounter((prev) => {
        const newCount = prev - 1;
        if (newCount <= 0) {
          setCanvasDragActive(false);
          return 0;
        }
        return newCount;
      });
    }
  }, []);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleCanvasDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Check if any modal is open
      const isModalOpen = document.querySelector('.MuiModal-backdrop, .MuiDialog-root, [role="dialog"]') !== null;

      // Only handle files if no modal is open
      if (!isModalOpen && e.dataTransfer.types.includes("Files")) {
        if (!canvasDragActive) {
          setCanvasDragActive(true);
        }
      }
    },
    [canvasDragActive]
  );

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleCanvasDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if any modal is open
    const isModalOpen = document.querySelector('.MuiModal-backdrop, .MuiDialog-root, [role="dialog"]') !== null;

    // Reset drag state regardless
    setCanvasDragActive(false);
    setCanvasDragCounter(0);

    // Only process files if no modal is open
    if (!isModalOpen && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);

      // Filter for supported file types
      const allowedTypes = [".txt", ".zip"];
      const validFiles = files.filter((file) => {
        const fileExtension = `.${file?.name?.split(".").pop()?.toLowerCase() || ''}`;
        return allowedTypes.includes(fileExtension);
      });

      if (validFiles.length > 0) {
        // Set the files to be imported and trigger the import dialog
        setPendingImportFiles(validFiles);

        // Trigger the import dialog to open
        if (importModuleRef.current) {
          importModuleRef.current.openWithFiles(validFiles);
        }
      }
    }
  }, []);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleImportModule = async (moduleDataArray) =>
    new Promise((resolve, reject) => {
      if (!Array.isArray(moduleDataArray) || moduleDataArray.length === 0) {
        reject(new Error("No module data provided"));
        return;
      }

      let totalModulesCreated = 0;

      const processNextModule = (index) => {
        if (index >= moduleDataArray.length) {
          // All modules processed
          if (totalModulesCreated > 0) {
            // Clear pending files after successful import
            setPendingImportFiles([]);

            // Disable all onboarding messages after successful import
            setShowOnboardingStep1(false);
            setShowOnboardingStep2(false);
            setShouldShowMessage(false);
            setIsFirstTimeUser(false);
            setHasEverAddedModule(true);
            resolve();
          } else {
            reject(new Error("No modules were successfully created"));
          }
          return;
        }

        const moduleData = moduleDataArray[index];

        try {
          // Handle module replacement if needed
          if (moduleData._isReplacement && moduleData._replaceModuleId) {
            // For replacement, first remove the existing module from all state arrays
            const moduleIdToReplace = moduleData._replaceModuleId;

            // Clean up the replacement flags before adding
            const cleanModuleData = { ...moduleData };
            delete cleanModuleData._isReplacement;
            delete cleanModuleData._replaceModuleId;

            // Remove from all state arrays synchronously with safety checks
            setModules((prev) => (Array.isArray(prev) ? prev.filter((item) => item.id !== moduleIdToReplace) : []));
            setModuleStates((prev) => (Array.isArray(prev) ? prev.filter((item) => item.id !== moduleIdToReplace) : []));
            setComponentStates((prev) => (Array.isArray(prev) ? prev.filter((item) => item.parentModuleId !== moduleIdToReplace) : []));
            setTier3States((prev) => (Array.isArray(prev) ? prev.filter((item) => item.parentModuleId !== moduleIdToReplace) : []));
            setSuperDataStructure((prev) => (Array.isArray(prev) ? prev.filter((item) => item.moduleId !== moduleIdToReplace) : []));

            // Use the existing function with cleaned data - it will add with the same ID
            addModule(cleanModuleData, false, false);
          } else {
            // Use the existing function with the parsed data
            addModule(moduleData, false, false);
          }

          totalModulesCreated += 1;

          // Small delay to allow state to settle before processing next module
          setTimeout(() => processNextModule(index + 1), 50);
        } catch (error) {
          console.warn(`Failed to create module from data:`, error);
          // Continue with next module even if one fails
          setTimeout(() => processNextModule(index + 1), 50);
        }
      };

      // Start processing modules
      processNextModule(0);
    });

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleAddExampleModule = (moduleData) => {
    addModule(moduleData, false, false, 0);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const getCurrentModuleData = () => {
    if (!selectedModule) return null;

    return (
      superDataStructure.find((m) => m.moduleId === selectedModule) || {
        moduleId: selectedModule,
        moduleName: selectedModuleName?.name || "",
        basics: {
          nTradingState: state_nTradingState,
          nNumber: state_nNumber,
          dVersion: state_dVersion,
          strIdeaOutputDescription: state_strIdeaOutputDescription,
        },
        //
        stopLossTrailings,
        takeProfitDynamics,
        tradeSplittings,
        //
        indicators,
        indicatorCombinations,
        chartAnalyzers,
      }
    );
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleCloseConfirmationDialog = () => {
    setConfirmationDialog((prev) => ({ ...prev, open: false }));
    // Clear content after dialog animation completes (typically 300ms)
    setTimeout(() => {
      setConfirmationDialog({ open: false, title: "", message: "", onConfirm: null, disabled: false });
    }, 300);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useEffect(() => {
    let timer;
    if (NEWSLETTER_DELAY_SECONDS > 0) {
      // Check if user has subscribed to newsletter
      const newsletterData = localStorage.getItem(STORAGE_KEYS.NEWSLETTER);
      let shouldShowNewsletter = true;

      if (newsletterData) {
        try {
          const { subscribed, dismissed, timestamp } = JSON.parse(newsletterData);
          const now = Date.now();
          const timeSinceAction = now - timestamp;
          
          if (subscribed) {
            // Check if 90 days have passed for subscribed users
            const thirtyDaysInMs = 90 * 24 * 60 * 60 * 1000;
            
            if (timeSinceAction < thirtyDaysInMs) {
              shouldShowNewsletter = false;
            } else {
              // Reset if 90 days have passed
              localStorage.removeItem(STORAGE_KEYS.NEWSLETTER);
            }
          } else if (dismissed) {
            // Check if 1 days have passed for dismissed (Maybe Later) users
            const waitDaysInMs = 1 * 24 * 60 * 60 * 1000;
            
            if (timeSinceAction < waitDaysInMs) {
              shouldShowNewsletter = false;
            } else {
              // Reset if 1 days have passed
              localStorage.removeItem(STORAGE_KEYS.NEWSLETTER);
            }
          }
        } catch (error) {
          console.error("Error parsing newsletter data:", error);
          localStorage.removeItem(STORAGE_KEYS.NEWSLETTER);
        }
      }

      if (shouldShowNewsletter) {
        timer = setTimeout(() => {
          setShowNewsletterDialog(true);
        }, NEWSLETTER_DELAY_SECONDS * 1000);
      }

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }
  }, []);

  // ----------------------------------------------------------------------
  // Section - Move Module Up
  // ----------------------------------------------------------------------
  const moveModuleUp = (moduleId) => {
    const currentIndex = modules.findIndex((m) => m.id === moduleId);
    if (currentIndex > 0) {
      const newModules = [...modules];
      [newModules[currentIndex - 1], newModules[currentIndex]] = [newModules[currentIndex], newModules[currentIndex - 1]];
      setModules(newModules);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const moveModuleDown = (moduleId) => {
    const currentIndex = modules.findIndex((m) => m.id === moduleId);
    if (currentIndex < modules.length - 1) {
      const newModules = [...modules];
      [newModules[currentIndex], newModules[currentIndex + 1]] = [newModules[currentIndex + 1], newModules[currentIndex]];
      setModules(newModules);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const moveItemUp = (array, setArray, itemId) => {
    const currentIndex = array.findIndex((item) => item.id === itemId);
    if (currentIndex > 0) {
      const newArray = [...array];
      [newArray[currentIndex - 1], newArray[currentIndex]] = [newArray[currentIndex], newArray[currentIndex - 1]];
      setArray(newArray);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const moveItemDown = (array, setArray, itemId) => {
    const currentIndex = array.findIndex((item) => item.id === itemId);
    if (currentIndex < array.length - 1) {
      const newArray = [...array];
      [newArray[currentIndex], newArray[currentIndex + 1]] = [newArray[currentIndex + 1], newArray[currentIndex]];
      setArray(newArray);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const duplicateModule = (moduleId) => {
    const moduleToDuplicate = modules.find((m) => m.id === moduleId);
    if (!moduleToDuplicate || modules.length >= LIMITS.MAX_MODULES) return;

    // Find the module's data in superDataStructure
    const moduleData = superDataStructure.find((m) => m.moduleId === moduleId);

    // Generate new unique ID and number
    const newModuleId = Date.now();
    let newModuleNumber = null;

    // Generate unique module number
    while (newModuleNumber === null) {
      let isUnique = true;
      const moduleNumberTest = Math.floor(Math.random() * (9999 - 1000 + 1) + 1000);
      modules.forEach((module) => {
        // Updated to extract number from new format
        const match = module?.name?.match(/Module (\d+)/);
        const existingNumber = match ? parseInt(match[1], 10) : null;
        if (moduleNumberTest === existingNumber) {
          isUnique = false;
        }
      });
      if (isUnique) {
        newModuleNumber = moduleNumberTest;
      }
    }

    // Create the duplicate module with the new name
    const newModuleName = `Module ${newModuleNumber}`;
    const duplicatedModule = {
      ...moduleToDuplicate,
      id: newModuleId,
      name: newModuleName,
      alreadyOpened: false,
    };

    // Store the current navigation state to restore later
    const currentNavigationState = {
      selectedComponent,
      selectedTier3,
      selectedTier4,
      //
      selectedStopLossTrailing,
      selectedTakeProfitDynamic,
      selectedTradeSplitting,
      //
      selectedIndicator,
      selectedIndicatorCombination,
      selectedChartAnalyzer,
    };

    // Save current module state before switching (if there's a currently selected module)
    if (selectedModule !== null) {
      const currentModuleKey = getModuleKey(selectedModule);
      setModuleStates((prev) => ({
        ...prev,
        [currentModuleKey]: currentNavigationState,
      }));
    }

    // Add the duplicated module
    setModules((prev) => [...prev, duplicatedModule]);

    // First, clear all selections to prevent undefined errors
    setSelectedModule(null);
    setSelectedComponent(null);
    setSelectedTier3(null);
    setSelectedTier4(null);
    //
    setSelectedStopLossTrailing(null);
    setSelectedTakeProfitDynamic(null);
    setSelectedTradeSplitting(null);
    //
    setSelectedIndicator(null);
    setSelectedIndicatorCombination(null);
    setSelectedChartAnalyzer(null);

    // Clear arrays temporarily to prevent stale references
    setStopLossTrailings([]);
    setTakeProfitDynamics([]);
    setTradeSplittings([]);
    //
    setIndicators([]);
    setIndicatorCombinations([]);
    setChartAnalyzers([]);

    // If we have module data, duplicate all its contents
    if (moduleData) {
      // Duplicate stop loss trailings with new IDs and preserve their configurations
      const duplicatedStopLossTrailings = moduleData.stopLossTrailings
        ? moduleData.stopLossTrailings.map((item) => ({
            ...item,
          }))
        : [];

      // Duplicate take profit dynamics with new IDs and preserve their configurations
      const duplicatedTakeProfitDynamics = moduleData.takeProfitDynamics
        ? moduleData.takeProfitDynamics.map((item) => ({
            ...item,
          }))
        : [];

      // Duplicate trade splittings with new IDs and preserve their configurations
      const duplicatedTradeSplittings = moduleData.tradeSplittings
        ? moduleData.tradeSplittings.map((item) => ({
            ...item,
          }))
        : [];

      // Duplicate indicators with new IDs and preserve their configurations
      const duplicatedIndicators = moduleData.indicators
        ? moduleData.indicators.map((item) => ({
            ...item,
          }))
        : [];

      // Duplicate indicator combinations with new IDs and preserve their configurations
      const duplicatedIndicatorCombinations = moduleData.indicatorCombinations
        ? moduleData.indicatorCombinations.map((item) => ({
            ...item,
          }))
        : [];

      // Duplicate chart analyzers with new IDs and preserve their configurations
      const duplicatedChartAnalyzers = moduleData.chartAnalyzers
        ? moduleData.chartAnalyzers.map((item) => ({
            ...item,
          }))
        : [];

      // Create new basics with the updated Number
      const newBasics = {
        ...moduleData.basics,
        nNumber: newModuleNumber,
      };

      setSuperDataStructure((prev) => [
        ...prev,
        {
          ...moduleData,
          moduleId: newModuleId,
          moduleName: newModuleName,
          basics: newBasics,
          //
          stopLossTrailings: duplicatedStopLossTrailings,
          takeProfitDynamics: duplicatedTakeProfitDynamics,
          tradeSplittings: duplicatedTradeSplittings,
          //
          indicators: duplicatedIndicators,
          indicatorCombinations: duplicatedIndicatorCombinations,
          chartAnalyzers: duplicatedChartAnalyzers,
        },
      ]);

      // Use setTimeout to ensure state updates are processed in the correct order
      setTimeout(() => {
        // Set the arrays with duplicated data
        setStopLossTrailings(duplicatedStopLossTrailings);
        setTakeProfitDynamics(duplicatedTakeProfitDynamics);
        setTradeSplittings(duplicatedTradeSplittings);
        //
        setIndicators(duplicatedIndicators);
        setIndicatorCombinations(duplicatedIndicatorCombinations);
        setChartAnalyzers(duplicatedChartAnalyzers);

        // Update the states with the new module number
        setState_nTradingState(moduleData.basics?.nTradingState || "1");
        setState_nNumber(newModuleNumber);
        setState_dVersion(moduleData.basics?.dVersion || "3.1");
        setState_strIdeaOutputDescription(moduleData.basics?.strIdeaOutputDescription || "");

        // Map the selected items to their duplicated counterparts
        let newSelectedStopLossTrailing = null;
        if (currentNavigationState.selectedStopLossTrailing) {
          const originalItem = moduleData.stopLossTrailings?.find((item) => item.id === currentNavigationState.selectedStopLossTrailing);
          if (originalItem) {
            const duplicatedItem = duplicatedStopLossTrailings.find((item) => item?.name === originalItem?.name);
            if (duplicatedItem) {
              newSelectedStopLossTrailing = duplicatedItem.id;
            }
          }
        }

        let newSelectedTakeProfitDynamic = null;
        if (currentNavigationState.selectedTakeProfitDynamic) {
          const originalItem = moduleData.takeProfitDynamics?.find((item) => item.id === currentNavigationState.selectedTakeProfitDynamic);
          if (originalItem) {
            const duplicatedItem = duplicatedTakeProfitDynamics.find((item) => item?.name === originalItem?.name);
            if (duplicatedItem) {
              newSelectedTakeProfitDynamic = duplicatedItem.id;
            }
          }
        }

        let newSelectedTradeSplitting = null;
        if (currentNavigationState.selectedTradeSplitting) {
          const originalItem = moduleData.tradeSplittings?.find((item) => item.id === currentNavigationState.selectedTradeSplitting);
          if (originalItem) {
            const duplicatedItem = duplicatedTradeSplittings.find((item) => item?.name === originalItem?.name);
            if (duplicatedItem) {
              newSelectedTradeSplitting = duplicatedItem.id;
            }
          }
        }

        let newSelectedIndicator = null;
        if (currentNavigationState.selectedIndicator) {
          const originalIndicator = moduleData.indicators?.find((ind) => ind.id === currentNavigationState.selectedIndicator);
          if (originalIndicator) {
            const duplicatedIndicator = duplicatedIndicators.find((ind) => ind?.name === originalIndicator?.name);
            if (duplicatedIndicator) {
              newSelectedIndicator = duplicatedIndicator.id;
            }
          }
        }

        let newSelectedIndicatorCombination = null;
        if (currentNavigationState.selectedIndicatorCombination) {
          const originalItem = moduleData.indicatorCombinations?.find((item) => item.id === currentNavigationState.selectedIndicatorCombination);
          if (originalItem) {
            const duplicatedItem = duplicatedIndicatorCombinations.find((item) => item?.name === originalItem?.name);
            if (duplicatedItem) {
              newSelectedIndicatorCombination = duplicatedItem.id;
            }
          }
        }

        let newSelectedChartAnalyzer = null;
        if (currentNavigationState.selectedChartAnalyzer) {
          const originalItem = moduleData.chartAnalyzers?.find((item) => item.id === currentNavigationState.selectedChartAnalyzer);
          if (originalItem) {
            const duplicatedItem = duplicatedChartAnalyzers.find((item) => item?.name === originalItem?.name);
            if (duplicatedItem) {
              newSelectedChartAnalyzer = duplicatedItem.id;
            }
          }
        }

        // Use another setTimeout to ensure arrays are fully updated before setting selections
        setTimeout(() => {
          // Set the new module as selected
          setSelectedModule(newModuleId);

          // Restore the navigation state with updated IDs
          setSelectedComponent(currentNavigationState.selectedComponent);
          setSelectedTier3(currentNavigationState.selectedTier3);
          setSelectedTier4(currentNavigationState.selectedTier4);
          //
          setSelectedStopLossTrailing(newSelectedStopLossTrailing);
          setSelectedTakeProfitDynamic(newSelectedTakeProfitDynamic);
          setSelectedTradeSplitting(newSelectedTradeSplitting);
          //
          setSelectedIndicator(newSelectedIndicator);
          setSelectedIndicatorCombination(newSelectedIndicatorCombination);
          setSelectedChartAnalyzer(newSelectedChartAnalyzer);

          // Load basics for the new module (this should use the updated basics)
          loadModuleBasics(newModuleId);

          // Save the navigation state for the new module so it can be restored later
          setTimeout(() => {
            const newModuleKey = getModuleKey(newModuleId);
            const newNavigationState = {
              selectedComponent: currentNavigationState.selectedComponent,
              selectedTier3: currentNavigationState.selectedTier3,
              selectedTier4: currentNavigationState.selectedTier4,
              //
              selectedStopLossTrailing: newSelectedStopLossTrailing,
              selectedTakeProfitDynamic: newSelectedTakeProfitDynamic,
              selectedTradeSplitting: newSelectedTradeSplitting,
              //
              selectedIndicator: newSelectedIndicator,
              selectedIndicatorCombination: newSelectedIndicatorCombination,
              selectedChartAnalyzer: newSelectedChartAnalyzer,
            };

            setModuleStates((prev) => ({
              ...prev,
              [newModuleKey]: newNavigationState,
            }));

            // Also save component and tier3 states for the new module to ensure proper restoration
            if (currentNavigationState.selectedComponent) {
              const newComponentKey = getComponentKey(newModuleId, currentNavigationState.selectedComponent);
              setComponentStates((prev) => ({
                ...prev,
                [newComponentKey]: {
                  selectedTier3: currentNavigationState.selectedTier3,
                  selectedTier4: currentNavigationState.selectedTier4,
                  //
                  selectedStopLossTrailing: newSelectedStopLossTrailing,
                  selectedTakeProfitDynamic: newSelectedTakeProfitDynamic,
                  selectedTradeSplitting: newSelectedTradeSplitting,
                  //
                  selectedIndicator: newSelectedIndicator,
                  selectedIndicatorCombination: newSelectedIndicatorCombination,
                  selectedChartAnalyzer: newSelectedChartAnalyzer,
                },
              }));

              if (currentNavigationState.selectedTier3) {
                const newTier3Key = getTier3Key(newModuleId, currentNavigationState.selectedComponent, currentNavigationState.selectedTier3);
                setTier3States((prev) => ({
                  ...prev,
                  [newTier3Key]: {
                    selectedStopLossTrailing: newSelectedStopLossTrailing,
                    selectedTakeProfitDynamic: newSelectedTakeProfitDynamic,
                    selectedTradeSplitting: newSelectedTradeSplitting,
                    //
                    selectedIndicator: newSelectedIndicator,
                    selectedIndicatorCombination: newSelectedIndicatorCombination,
                    selectedChartAnalyzer: newSelectedChartAnalyzer,
                  },
                }));
              }
            }

            // Mark the new module as already opened to prevent default navigation overrides
            setModules((prev) => prev.map((module) => (module.id === newModuleId ? { ...module, alreadyOpened: true } : module)));
          }, 10);
        }, 50);
      }, 50);
    } else {
      // If no module data exists, just select the new module with default navigation
      setTimeout(() => {
        setSelectedModule(newModuleId);
        setState_nTradingState("1");
        setState_nNumber(newModuleNumber);
        setState_dVersion("3.1");
        setState_strIdeaOutputDescription("");

        // Keep the same navigation state but clear selections since no data exists
        setSelectedComponent(currentNavigationState.selectedComponent);
        setSelectedTier3(currentNavigationState.selectedTier3);
        setSelectedTier4(null);
        //
        setSelectedStopLossTrailing(null);
        setSelectedTakeProfitDynamic(null);
        setSelectedTradeSplitting(null);
        //
        setSelectedIndicator(null);
        setSelectedIndicatorCombination(null);
        setSelectedChartAnalyzer(null);
      }, 100);
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const duplicateStopLossTrailing = (itemId) => {
    if (stopLossTrailings.length >= LIMITS.MAX_STOP_LOSS_TRAILINGS) return;

    const itemToDuplicate = stopLossTrailings.find((item) => item.id === itemId);
    if (!itemToDuplicate) return;

    const newItemId = Date.now();
    const duplicatedItem = {
      ...itemToDuplicate,
      id: newItemId,
      name: `${itemToDuplicate?.name || 'Unnamed'}`,
    };

    setStopLossTrailings((prev) => [...prev, duplicatedItem]);

    // Auto-select the newly duplicated item
    setSelectedStopLossTrailing(newItemId);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const duplicateTakeProfitDynamic = (itemId) => {
    if (takeProfitDynamics.length >= LIMITS.MAX_TAKE_PROFIT_DYNAMICS) return;

    const itemToDuplicate = takeProfitDynamics.find((item) => item.id === itemId);
    if (!itemToDuplicate) return;

    const newItemId = Date.now();
    const duplicatedItem = {
      ...itemToDuplicate,
      id: newItemId,
      name: `${itemToDuplicate?.name || 'Unnamed'}`,
    };

    setTakeProfitDynamics((prev) => [...prev, duplicatedItem]);

    // Auto-select the newly duplicated item
    setSelectedTakeProfitDynamic(newItemId);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const duplicateTradeSplitting = (itemId) => {
    if (tradeSplittings.length >= LIMITS.MAX_TRADE_SPLITTINGS) return;

    const itemToDuplicate = tradeSplittings.find((item) => item.id === itemId);
    if (!itemToDuplicate) return;

    const newItemId = Date.now();
    const duplicatedItem = {
      ...itemToDuplicate,
      id: newItemId,
      name: `${itemToDuplicate?.name || 'Unnamed'}`,
    };

    setTradeSplittings((prev) => [...prev, duplicatedItem]);

    // Auto-select the newly duplicated item
    setSelectedTradeSplitting(newItemId);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const duplicateIndicator = (indicatorId) => {
    if (indicators.length >= LIMITS.MAX_INDICATORS) return;

    const indicatorToDuplicate = indicators.find((item) => item.id === indicatorId);
    if (!indicatorToDuplicate) return;

    // Generate new unique ID
    const newItemId = Date.now();

    // Create the duplicated indicator with its config
    const duplicatedIndicator = {
      ...indicatorToDuplicate,
      id: newItemId,
      name: `${indicatorToDuplicate?.name || 'Unnamed Indicator'}`,
      isNewlyCreated: false,
      config: indicatorToDuplicate.config ? { ...indicatorToDuplicate.config } : null,
    };

    // Add the duplicated indicator
    setIndicators((prev) => [...prev, duplicatedIndicator]);

    // Auto-select the newly duplicated indicator
    setSelectedIndicator(newItemId);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const duplicateIndicatorCombination = (indicatorCombinationId) => {
    if (indicatorCombinations.length >= LIMITS.MAX_INDICATOR_COMBINATIONS) return;

    const indicatorCombinationToDuplicate = indicatorCombinations.find((item) => item.id === indicatorCombinationId);
    if (!indicatorCombinationToDuplicate) return;

    // Generate new unique ID
    const newItemId = Date.now();

    // Create the duplicated indicator combination with its config
    const duplicatedIndicatorCombination = {
      ...indicatorCombinationToDuplicate,
      id: newItemId,
      name: `${indicatorCombinationToDuplicate?.name || 'Unnamed Combination'}`,
      isNewlyCreated: false,
      config: indicatorCombinationToDuplicate.config ? { ...indicatorCombinationToDuplicate.config } : null,
    };

    // Add the duplicated indicator combination
    setIndicatorCombinations((prev) => [...prev, duplicatedIndicatorCombination]);

    // Auto-select the newly duplicated indicator combination
    setSelectedIndicatorCombination(newItemId);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const duplicateChartAnalyzer = (chartAnalyzerId) => {
    if (chartAnalyzers.length >= LIMITS.MAX_CHART_ANALYZERS) return;

    const chartAnalyzerToDuplicate = chartAnalyzers.find((item) => item.id === chartAnalyzerId);
    if (!chartAnalyzerToDuplicate) return;

    // Generate new unique ID
    const newItemId = Date.now();

    // Create the duplicated chart analyzer with its config
    const duplicatedChartAnalyzer = {
      ...chartAnalyzerToDuplicate,
      id: newItemId,
      name: `${chartAnalyzerToDuplicate?.name || 'Unnamed Analyzer'}`,
      isNewlyCreated: false,
      config: chartAnalyzerToDuplicate.config ? { ...chartAnalyzerToDuplicate.config } : null,
    };

    // Add the duplicated chart analyzer
    setChartAnalyzers((prev) => [...prev, duplicatedChartAnalyzer]);

    // Auto-select the newly duplicated chart analyzer
    setSelectedChartAnalyzer(newItemId);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  function ItemPopover({ popoverState, setPopoverState, onRemove, onMoveUp, onMoveDown, onDuplicate, onExport, itemName }) {
    const handleClose = () => {
      setPopoverState({ anchorEl: null, itemId: null });
    };

    const handleAction = (action) => {
      action();
      handleClose();
    };

    return (
      <Popover
        open={Boolean(popoverState.anchorEl)}
        anchorEl={popoverState.anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: "auto",
            maxWidth: 250,
            boxShadow: "0px 0px 2px 0px rgb(0 0 0 / 50%)",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="contentSmall" sx={{ fontWeight: 600 }}>
            {itemName}
          </Typography>

          <Stack spacing={1} sx={{ mt: 1 }}>
            <B3Tiny
              disableRipple={Boolean(true)}
              variant="outlined"
              onClick={() => handleAction(onMoveUp)}
              fullWidth
              startIcon={<ArrowUpwardIcon fontSize="small" />}
              sx={{ justifyContent: "flex-start" }}
            >
              Move up
            </B3Tiny>

            <B3Tiny
              disableRipple={Boolean(true)}
              variant="outlined"
              onClick={() => handleAction(onMoveDown)}
              fullWidth
              startIcon={<ArrowDownwardIcon fontSize="small" />}
              sx={{ justifyContent: "flex-start" }}
            >
              Move down
            </B3Tiny>

            <B3Tiny
              disableRipple={Boolean(true)}
              variant="outlined"
              onClick={() => handleAction(onDuplicate)}
              fullWidth
              startIcon={<ContentCopyIcon fontSize="small" />}
              sx={{ justifyContent: "flex-start" }}
            >
              Duplicate
            </B3Tiny>

            {onExport && (
              <B3Tiny
                disableRipple={Boolean(true)}
                variant="outlined"
                onClick={() => handleAction(onExport)}
                fullWidth
                startIcon={<DownloadIcon fontSize="small" />}
                sx={{ justifyContent: "flex-start" }}
              >
                Export
              </B3Tiny>
            )}

            <B3Tiny
              disableRipple={Boolean(true)}
              variant="outlined"
              onClick={() => handleAction(onRemove)}
              fullWidth
              startIcon={<DeleteIcon fontSize="small" />}
              sx={{ justifyContent: "flex-start" }}
            >
              Delete
            </B3Tiny>
          </Stack>
        </Box>
      </Popover>
    );
  }

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const headerHeight = !isMobile
    ? defines.headerHeightDesktop + defines.footerHeightDesktop
    : defines.headerHeightMobile + defines.footerHeightMobile;

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const updateModuleName = (numberValue) => {
    setModules((prev) =>
      prev.map((module) => {
        if (module.id === selectedModule) {
          // Extract existing description from current name
          const currentName = module?.name || '';
          const match = currentName.match(/Module \d+: (.+)/);
          const description = match ? match[1] : "";

          // Create new name with preserved description
          const newName = description ? `Module ${numberValue}: ${description}` : `Module ${numberValue}`;

          return { ...module, name: newName };
        }
        return module;
      })
    );
  };

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  // eslint-disable-next-line
  return useMemo(() => {
    return !loadingSpinner ? (
      <Box
        sx={{
          position: "relative",
          minWidth: `calc(100vw - 10px)`,
          minHeight: `calc(100vh - ${headerHeight}px)`,
        }}
        onDragEnter={handleCanvasDragEnter}
        onDragLeave={handleCanvasDragLeave}
        onDragOver={handleCanvasDragOver}
        onDrop={handleCanvasDrop}
      >
        {/* Newsletter dialog */}
        <HandleNewsletter 
          open={showNewsletterDialog} 
          onClose={() => setShowNewsletterDialog(false)} 
          storageKey={STORAGE_KEYS.NEWSLETTER}
        />

        {/* Confirmation dialog */}
        <HandleConfirmationDialog
          open={confirmationDialog.open}
          onClose={handleCloseConfirmationDialog}
          title={confirmationDialog.title}
          message={confirmationDialog.message}
          onConfirm={confirmationDialog.onConfirm}
          confirmText={confirmationDialog.confirmText}
          cancelText={confirmationDialog.cancelText}
          disabled={confirmationDialog.disabled}
        />

        {/* Drag overlay for better visual feedback */}
        {canvasDragActive && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(100, 200, 255, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10001,
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                backgroundColor: "#F2F2F2",
                borderRadius: "10px",
                padding: 3,
                textAlign: "center",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="contentMiddle"
                sx={{
                  fontWeight: 600,
                }}
              >
                Drop files to import modules
              </Typography>

              <Typography variant="contentSmall">Supported formats: .txt, .zip</Typography>
            </Box>
          </Box>
        )}
        <FormikProvider value={formik}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "flex-start",
              gap: 2,
              padding: 2,
            }}
          >
            {/* Session restored message */}
            {showSessionRestoredMessage && (
              <Box
                sx={{
                  position: "fixed",
                  bottom: "30px",
                  // Desktop: right-aligned, Mobile: centered with margins
                  ...(!isMobile ? { right: "30px" } : { left: "20px", right: "30px" }),
                  backgroundColor: "#a8e6cf",
                  color: "inherit",
                  padding: "8px 16px",
                  borderRadius: "10px",
                  zIndex: 10000,
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
                  animation: "fadeInOut 3s ease-in-out forwards",
                  "@keyframes fadeInOut": {
                    "0%": { opacity: 0, transform: "translateY(20px)" },
                    "10%": { opacity: 1, transform: "translateY(0)" },
                    "90%": { opacity: 1, transform: "translateY(0)" },
                    "100%": { opacity: 0, transform: "translateY(20px)" },
                  },
                }}
                onAnimationEnd={() => setShowSessionRestoredMessage(false)}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: !isMobile ? "flex-start" : "center",
                    textAlign: !isMobile ? "left" : "center",
                  }}
                >
                  <Typography variant="contentMiddle">Session successfully restored</Typography>
                </Box>
              </Box>
            )}
            {/* ---------------------------------------------------------------------- */}
            {/* Onboarding messages */}
            {/* ---------------------------------------------------------------------- */}
            {showOnboardingStep1 && modules.length === 0 && !isMobile && (
              <>
                {/* ---------------------------------------------------------------------- */}
                {/* Message 1a */}
                {/* ---------------------------------------------------------------------- */}
                <OnboardingArrow
                  direction="arrowLeftUp"
                  color="white"
                  style={{
                    top: "330px",
                    left: "9.0%",
                  }}
                />
                <OnboardingMessage
                  style={{
                    top: "362px",
                    left: "calc(9.0% + 100px)",
                  }}
                >
                  Start here!
                  <br />
                  Add your first module.
                </OnboardingMessage>
                {/* ---------------------------------------------------------------------- */}
                {/* Message 1b */}
                {/* ---------------------------------------------------------------------- */}
                <>
                  <OnboardingArrow
                    direction="arrowRightUp"
                    color="white"
                    style={{
                      top: "10px",
                      right: `${480}px`,
                    }}
                  />
                  <OnboardingMessage
                    style={{
                      top: "45px",
                      right: `${580}px`,
                      width: "290px",
                    }}
                  >
                    Get familiar with the essentials and start building your first trading module.
                  </OnboardingMessage>
                </>
              </>
            )}

            {/* ---------------------------------------------------------------------- */}
            {/* Onboarding messages */}
            {/* ---------------------------------------------------------------------- */}
            {showOnboardingStep2 && !isMobile && (
              <>
                {/* ---------------------------------------------------------------------- */}
                {/* Message 2a */}
                {/* ---------------------------------------------------------------------- */}
                <OnboardingArrow
                  direction="arrowLeftUp"
                  color="white"
                  style={{
                    top: "680px",
                    left: "29%",
                  }}
                />
                <OnboardingMessage
                  style={{
                    top: "715px",
                    left: "calc(29% + 100px)",
                    width: "500px",
                  }}
                >
                  Select the component for order type, entry and exit signals, stop loss, take profit, and more right here.
                </OnboardingMessage>

                {/* ---------------------------------------------------------------------- */}
                {/* Message 2b */}
                {/* ---------------------------------------------------------------------- */}
                <OnboardingArrow
                  direction="arrowLeftUp"
                  color="white"
                  style={{
                    top: "420px",
                    left: "49%",
                  }}
                />
                <OnboardingMessage
                  style={{
                    top: "465px",
                    left: "calc(49% + 100px)",
                    width: "450px",
                  }}
                >
                  The signal generation view is already open!
                  <br />
                  Here you configure when trades open and close.
                </OnboardingMessage>

                {/* ---------------------------------------------------------------------- */}
                {/* Message 2c */}
                {/* ---------------------------------------------------------------------- */}
                <OnboardingArrow
                  direction="arrowLeftUp"
                  color="white"
                  style={{
                    top: "200px",
                    left: "69.0%",
                  }}
                />
                <OnboardingMessage
                  style={{
                    top: "245px",
                    left: "calc(69.0% + 100px)",
                    width: "300px",
                  }}
                >
                  Add your first indicator or load a example configuration.
                </OnboardingMessage>
              </>
            )}

            {/* ---------------------------------------------------------------------- */}
            {/* Marketing message - Only visible when no modules exist */}
            {/* ---------------------------------------------------------------------- */}
            {shouldShowMessage && !isMobile && (
              <Box
                sx={{
                  position: "fixed",
                  top: messagePosition.top,
                  left: messagePosition.left,
                  transform: "translate(-50%, -50%)",
                  maxWidth: "600px",
                  textAlign: "center",
                  zIndex: 10,
                  pointerEvents: "none",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "130%",
                    height: "130%",
                    background:
                      "radial-gradient(circle, rgba(100, 200, 255, 0.08) 0%, rgba(64, 180, 255, 0.04) 30%, rgba(0, 150, 255, 0.02) 60%, transparent 80%)",
                    borderRadius: "50%",
                    filter: "blur(25px)",
                    zIndex: -1,
                    opacity: isVisible ? 1 : 0,
                    transition: "opacity 0.5s ease-in-out",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "110%",
                    height: "110%",
                    background: "radial-gradient(circle, rgba(150, 220, 255, 0.12) 0%, rgba(100, 200, 255, 0.06) 40%, transparent 70%)",
                    borderRadius: "50%",
                    filter: "blur(15px)",
                    zIndex: -1,
                    opacity: isVisible ? 1 : 0,
                    transition: "opacity 0.5s ease-in-out",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "35px",
                    fontWeight: 300,
                    color: "rgba(255, 255, 255, 0.9)",
                    opacity: isVisible ? 1 : 0,
                    transition: "opacity 0.5s ease-in-out",
                    textShadow: "0 2px 4px rgba(0,0,0,0.3), 0 0 15px rgba(100, 200, 255, 0.2), 0 0 25px rgba(64, 180, 255, 0.1)",
                    lineHeight: 1.2,
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {MARKETING_MESSAGES[currentMessageIndex]}
                </Typography>
              </Box>
            )}

            {/* ---------------------------------------------------------------------- */}
            {/* Tier 1 */}
            {/* ---------------------------------------------------------------------- */}
            <Box
              sx={{
                flex: isMobile ? "none" : "1 0 0",
                width: isMobile ? "100%" : "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignSelf: isMobile ? "stretch" : "flex-start",
              }}
            >
              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              <Card
                sx={{
                  display: "flex",
                  pb: 1,
                  flexDirection: "column",
                  borderRadius: "10px",
                  boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                  transition: "box-shadow 0.3s ease-in-out",
                  backgroundColor: "#C8C8C8",
                  "&:hover": {
                    boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                  },
                }}
              >
                <Stack>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Head */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      pb: "10px",
                      px: 2,
                      mb: 0,
                      backgroundColor: "#C8C8C8",
                      borderRadius: "10px 10px 0px 0px",
                    }}
                    display="block"
                    align="center"
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        alignContent: "center",
                        pt: 2,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr:1 }}>
                        <Typography variant="h5" color="black" fontWeight={600} display="block" align="left">
                          Professional Mode
                        </Typography>

                        <SmartParameter value="" helpReference="ExpertAdvisorBuilder" helpText="" helpLink="" fieldName="professionalMode" iconColor="black" touched={touched} errors={errors} />
                      </Box>

                      <ModalContentSwitch
                        checked={professionalMode}
                        onChange={(event) => {
                          setProfessionalMode(event.target.checked);
                          if (selectedComponent === COMPONENTS[1]) {
                            setSelectedComponent(null);
                          }
                        }}
                        inputProps={{ "aria-label": "controlled" }}
                      />
                    </Box>
                  </Box>
                </Stack>
              </Card>

              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              <Card
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "10px",
                  boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                  transition: "box-shadow 0.3s ease-in-out",
                  "&:hover": {
                    boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                  },
                }}
              >
                <Stack>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Head */}
                  {/* ---------------------------------------------------------------------- */}
                  <CardHeader
                    title="Trading modules"
                    subtitle={`${modules.length} / ${LIMITS.MAX_MODULES}`}
                    helpReference="ExpertAdvisorBuilder"
                    helpText=""
                    helpLink=""
                    titleVariant="h5"
                    fieldName="Trading Modules"
                    touched={touched}
                    errors={errors}
                  />

                  <Box
                    sx={{
                      p: 2,
                      pt: 0,
                      maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Content */}
                    {/* ---------------------------------------------------------------------- */}
                    <Stack spacing={1}>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Action */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <HandleImportModule
                          ref={importModuleRef}
                          onImport={handleImportModule}
                          pendingFiles={pendingImportFiles}
                          onClearPendingFiles={() => setPendingImportFiles([])}
                          existingModules={superDataStructure || []}
                          duplicateAction={duplicateAction}
                          onDuplicateActionChange={setDuplicateAction}
                          maxModules={LIMITS.MAX_MODULES}
                        />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Action */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <HandleAddExampleModule 
                          onAddExample={handleAddExampleModule} 
                          existingModules={superDataStructure || []}
                          maxModules={LIMITS.MAX_MODULES}
                        />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Action */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <B1Tiny fullWidth onClick={() => addModule(null, true, true, 0)} disabled={modules.length >= LIMITS.MAX_MODULES}>
                          Add new module
                        </B1Tiny>
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Modules */}
                      {/* ---------------------------------------------------------------------- */}
                      {modules.map((item) => (
                        <Box key={item.id} variant="outlined" sx={{}}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            {selectedModule === item.id ? (
                              <B2Tiny
                                disableRipple={Boolean(true)}
                                fullWidth
                                sx={{
                                  mr: "3px",
                                }}
                                onClick={() => handleTradingModuleSelection(item.id)}
                              >
                                {item?.name || 'Unnamed'}
                              </B2Tiny>
                            ) : (
                              <B3Tiny
                                disableRipple={Boolean(true)}
                                variant="outlined"
                                fullWidth
                                sx={{
                                  mr: "3px",
                                }}
                                onClick={() => handleTradingModuleSelection(item.id)}
                              >
                                {item?.name || 'Unnamed'}
                              </B3Tiny>
                            )}

                            <IconButton
                              size="small"
                              onClick={(event) => {
                                setModulePopover({ anchorEl: event.currentTarget, itemId: item.id });
                              }}
                            >
                              <MoreHoriz fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}

                      {/* Module Popover */}
                      <ItemPopover
                        popoverState={modulePopover}
                        setPopoverState={setModulePopover}
                        onRemove={() => removeSpecificModule(modulePopover.itemId)}
                        onMoveUp={() => moveModuleUp(modulePopover.itemId)}
                        onMoveDown={() => moveModuleDown(modulePopover.itemId)}
                        onDuplicate={() => duplicateModule(modulePopover.itemId)}
                        onExport={() => exportModule(modulePopover.itemId)}
                        itemName={modules.find((m) => m.id === modulePopover.itemId)?.name || ""}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Card>

              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedModule !== null ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title="Basics"
                      subtitle={selectedModuleName ? selectedModuleName.name : ""}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName="Basics"
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* Trading state */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter value="Trading state" helpReference="ExpertAdvisorBuilder" helpText="" helpLink="" fieldName="nTradingState" touched={touched} errors={errors} />
                          <FormControl fullWidth>
                            <Select
                              {...getFieldProps("nTradingState")}
                              value={state_nTradingState}
                              input={<ModalContentOutlinedInput />}
                              error={Boolean(touched.nTradingState && errors.nTradingState)}
                              onChange={(event) => {
                                const newValue = event.target.value;
                                setState_nTradingState(newValue);
                                setFieldValue("nTradingState", newValue);
                              }}
                            >
                              <ModalContentMenuItem value="0">Excluded</ModalContentMenuItem>
                              <ModalContentMenuItem value="1">Enabled</ModalContentMenuItem>
                              <ModalContentMenuItem value="2">Disabled without closing</ModalContentMenuItem>
                              <ModalContentMenuItem value="3">Disabled with closing</ModalContentMenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* Number */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter value="Number" helpReference="ExpertAdvisorBuilder" helpText="" helpLink="" fieldName="nNumber" touched={touched} errors={errors} />
                          <ModalContentTextField
                            {...getFieldProps("nNumber")}
                            value={state_nNumber}
                            onChange={(event) => {
                              // Allow any input during typing - no validation here
                              const newValue = event.target.value;
                              setState_nNumber(newValue);
                            }}
                            onBlur={(event) => {
                              const inputValue = event.target.value;
                              
                              // Handle empty input
                              if (inputValue === "" || inputValue === null || inputValue === undefined) {
                                setState_nNumber("");
                                setFieldValue("nNumber", "");
                                return;
                              }

                              // Convert to number and validate
                              const numberValue = parseInt(inputValue, 10);
                              
                              // Check if it's a valid number
                              if (isNaN(numberValue)) {
                                // Reset to previous valid value or empty
                                setState_nNumber("");
                                setFieldValue("nNumber", "");
                                return;
                              }

                              // Check if within range
                              if (numberValue < 1 || numberValue > 9999999999999) {
                                // Clamp to valid range
                                const clampedValue = Math.max(1, Math.min(9999999999999, numberValue));
                                const finalValue = !isNumberUnique(modules, clampedValue, selectedModule) 
                                  ? findNextAvailableNumber(modules, clampedValue, selectedModule, "up") || clampedValue
                                  : clampedValue;
                                
                                setState_nNumber(finalValue.toString());
                                setFieldValue("nNumber", finalValue.toString());
                                updateModuleName(finalValue);
                                return;
                              }

                              // Check if number is unique
                              if (!isNumberUnique(modules, numberValue, selectedModule)) {
                                // Find next available number (prefer going up)
                                const nextAvailable = findNextAvailableNumber(modules, numberValue, selectedModule, "up");
                                
                                if (nextAvailable !== null) {
                                  setState_nNumber(nextAvailable.toString());
                                  setFieldValue("nNumber", nextAvailable.toString());
                                  updateModuleName(nextAvailable);
                                } else {
                                  // Fallback: try going down
                                  const prevAvailable = findNextAvailableNumber(modules, numberValue, selectedModule, "down");
                                  if (prevAvailable !== null) {
                                    setState_nNumber(prevAvailable.toString());
                                    setFieldValue("nNumber", prevAvailable.toString());
                                    updateModuleName(prevAvailable);
                                  }
                                }
                              } else {
                                // Number is unique and valid, use it
                                setState_nNumber(numberValue.toString());
                                setFieldValue("nNumber", numberValue.toString());
                                updateModuleName(numberValue);
                              }
                            }}
                            fullWidth
                            size="small"
                            type="number"
                            inputProps={{
                              min: 1,
                              max: 9999999999999,
                              step: 1,
                            }}
                            error={Boolean(touched.nNumber && errors.nNumber)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dVersion */}
                        {/* ---------------------------------------------------------------------- */}
                        {/* Not needed */}

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* strIdeaOutputDescription */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Description"
                            helpReference="ExpertAdvisorBuilder" 
                            helpText=""
                            helpLink=""
                            fieldName="strIdeaOutputDescription"
                            touched={touched}
                            errors={errors}
                          />
                          <ModalContentTextField
                            {...getFieldProps("strIdeaOutputDescription")}
                            value={state_strIdeaOutputDescription}
                            onChange={(event) => {
                              const newValue = event.target.value;

                              // Filter out '=' and '_' characters
                              const filteredValue = newValue.replace(/[=_]/g, "");

                              // Only update if within character limit
                              if (filteredValue.length <= 50) {
                                setState_strIdeaOutputDescription(filteredValue);
                                clearTimeout(window.formikTimeout);
                                window.formikTimeout = setTimeout(() => {
                                  setFieldValue("strIdeaOutputDescription", filteredValue);
                                }, 300);
                              }
                              // If exceeding limit, don't update the state, effectively preventing the input
                            }}
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            type="text"
                            error={Boolean(touched.strIdeaOutputDescription && errors.strIdeaOutputDescription)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              maxLength: 50,
                            }}
                          />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box sx={{ mt: 1 }} />
                        <Box>
                          {selectedModule && (
                            <HandlePrintView
                              moduleData={getCurrentModuleData()}
                              moduleName={selectedModuleName?.name || "Untitled Module"}
                              professionalMode={professionalMode}
                            />
                          )}
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>{selectedModule && <HandlePreviewFileContent moduleData={getCurrentModuleData()} />}</Box>
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}
            </Box>

            {/* ---------------------------------------------------------------------- */}
            {/* Tier 2 */}
            {/* ---------------------------------------------------------------------- */}
            <Box
              sx={{
                flex: isMobile ? "none" : "1 0 0",
                width: isMobile ? "100%" : "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignSelf: isMobile ? "stretch" : "flex-start",
              }}
            >
              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedModule !== null ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title="Components"
                      subtitle={selectedModuleName ? selectedModuleName.name : ""}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName="Components"
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {COMPONENTS.map((component, index) => (
                          <React.Fragment key={index}>
                            {professionalMode === true ? (
                              <Box key={index}>
                                {selectedComponent === component ? (
                                  <B2Tiny
                                    disableRipple={Boolean(true)}
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => handleComponentSelection(component)}
                                  >
                                    {component}
                                  </B2Tiny>
                                ) : (
                                  <B3Tiny
                                    disableRipple={Boolean(true)}
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => handleComponentSelection(component)}
                                  >
                                    {component}
                                  </B3Tiny>
                                )}
                              </Box>
                            ) : (
                              <>
                                {component !== COMPONENTS[1] ? (
                                  <Box key={index}>
                                    {selectedComponent === component ? (
                                      <B2Tiny
                                        disableRipple={Boolean(true)}
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => handleComponentSelection(component)}
                                      >
                                        {component}
                                      </B2Tiny>
                                    ) : (
                                      <B3Tiny
                                        disableRipple={Boolean(true)}
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => handleComponentSelection(component)}
                                      >
                                        {component}
                                      </B3Tiny>
                                    )}
                                  </Box>
                                ) : null}
                              </>
                            )}
                          </React.Fragment>
                        ))}
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}
            </Box>

            {/* ---------------------------------------------------------------------- */}
            {/* Tier 3 */}
            {/* ---------------------------------------------------------------------- */}
            <Box
              sx={{
                flex: isMobile ? "none" : "1 0 0",
                width: isMobile ? "100%" : "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignSelf: isMobile ? "stretch" : "flex-start",
              }}
            >
              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedComponent !== null ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    {selectedComponent === COMPONENTS[0] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={selectedModuleName ? selectedModuleName.name : ""}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[1] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={selectedModuleName ? selectedModuleName.name : ""}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[2] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={selectedModuleName ? selectedModuleName.name : ""}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[3] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={selectedModuleName ? selectedModuleName.name : ""}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[4] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={`${stopLossTrailings.length} / ${LIMITS.MAX_STOP_LOSS_TRAILINGS}`}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[5] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={selectedModuleName ? selectedModuleName.name : ""}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[6] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={`${takeProfitDynamics.length} / ${LIMITS.MAX_TAKE_PROFIT_DYNAMICS}`}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[7] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={selectedModuleName ? selectedModuleName.name : ""}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[8] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={selectedModuleName ? selectedModuleName.name : ""}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[9] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={`${tradeSplittings.length} / ${LIMITS.MAX_TRADE_SPLITTINGS}`}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[10] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={selectedModuleName ? selectedModuleName.name : ""}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[11] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={selectedModuleName ? selectedModuleName.name : ""}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[12] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={selectedModuleName ? selectedModuleName.name : ""}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[13] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={selectedModuleName ? selectedModuleName.name : ""}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    {selectedComponent === COMPONENTS[14] ? (
                      <CardHeader
                        title={selectedComponent}
                        subtitle={selectedModuleName ? selectedModuleName.name : ""}
                        helpReference="ExpertAdvisorBuilder"
                        helpText=""
                        helpLink=""
                        titleVariant="h5"
                        fieldName={selectedComponent}
                        touched={touched}
                        errors={errors}
                      />
                    ) : null}

                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[0] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <TradeBasicsConfigure
                            onSave={(configData) => {
                              setTradeBasics(configData);
                            }}
                            initialData={tradeBasics}
                            professionalMode={professionalMode}
                          />

                          {/* ---------------------------------------------------------------------- */}
                          {/* Render */}
                          {/* ---------------------------------------------------------------------- */}
                          {TradeBasicsRenderParameters(professionalMode, tradeBasics)}
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[1] && professionalMode === true ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <PendingOrderSettingsConfigure
                            indicators={indicators}
                            chartAnalyzers={chartAnalyzers}
                            onSave={(configData) => {
                              setPendingOrderSettings(configData);
                            }}
                            initialData={pendingOrderSettings}
                            professionalMode={professionalMode}
                            nOperation={tradeBasics.nOperation}
                          />

                          {/* ---------------------------------------------------------------------- */}
                          {/* Render */}
                          {/* ---------------------------------------------------------------------- */}
                          {PendingOrderSettingsRenderParameters(professionalMode, pendingOrderSettings, indicators, chartAnalyzers)}
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[2] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            {selectedTier3 === "Indicators" ? (
                              <B2Tiny disableRipple={Boolean(true)} variant="outlined" fullWidth onClick={() => handleTier3Selection("Indicators")}>
                                Indicators
                              </B2Tiny>
                            ) : (
                              <B3Tiny disableRipple={Boolean(true)} variant="outlined" fullWidth onClick={() => handleTier3Selection("Indicators")}>
                                Indicators
                              </B3Tiny>
                            )}
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            {selectedTier3 === "Indicator Combinations" ? (
                              <B2Tiny
                                disableRipple={Boolean(true)}
                                variant="outlined"
                                fullWidth
                                onClick={() => handleTier3Selection("Indicator Combinations")}
                              >
                                Indicator combinations
                              </B2Tiny>
                            ) : (
                              <B3Tiny
                                disableRipple={Boolean(true)}
                                variant="outlined"
                                fullWidth
                                onClick={() => handleTier3Selection("Indicator Combinations")}
                              >
                                Indicator combinations
                              </B3Tiny>
                            )}
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            {selectedTier3 === "Chart Analyzers" ? (
                              <B2Tiny
                                disableRipple={Boolean(true)}
                                variant="outlined"
                                fullWidth
                                onClick={() => handleTier3Selection("Chart Analyzers")}
                              >
                                Chart analyzers
                              </B2Tiny>
                            ) : (
                              <B3Tiny
                                disableRipple={Boolean(true)}
                                variant="outlined"
                                fullWidth
                                onClick={() => handleTier3Selection("Chart Analyzers")}
                              >
                                Chart analyzers
                              </B3Tiny>
                            )}
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            {selectedTier3 === "News" ? (
                              <B2Tiny disableRipple={Boolean(true)} variant="outlined" fullWidth onClick={() => handleTier3Selection("News")}>
                                News
                              </B2Tiny>
                            ) : (
                              <B3Tiny disableRipple={Boolean(true)} variant="outlined" fullWidth onClick={() => handleTier3Selection("News")}>
                                News
                              </B3Tiny>
                            )}
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            {selectedTier3 === "Candle Patterns" ? (
                              <B2Tiny
                                disableRipple={Boolean(true)}
                                variant="outlined"
                                fullWidth
                                onClick={() => handleTier3Selection("Candle Patterns")}
                              >
                                Candle patterns
                              </B2Tiny>
                            ) : (
                              <B3Tiny
                                disableRipple={Boolean(true)}
                                variant="outlined"
                                fullWidth
                                onClick={() => handleTier3Selection("Candle Patterns")}
                              >
                                Candle patterns
                              </B3Tiny>
                            )}
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            {selectedTier3 === "Chart Patterns" ? (
                              <B2Tiny
                                disableRipple={Boolean(true)}
                                variant="outlined"
                                fullWidth
                                onClick={() => handleTier3Selection("Chart Patterns")}
                              >
                                Chart patterns
                              </B2Tiny>
                            ) : (
                              <B3Tiny
                                disableRipple={Boolean(true)}
                                variant="outlined"
                                fullWidth
                                onClick={() => handleTier3Selection("Chart Patterns")}
                              >
                                Chart patterns
                              </B3Tiny>
                            )}
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            {selectedTier3 === "Fibonacci Patterns" ? (
                              <B2Tiny
                                disableRipple={Boolean(true)}
                                variant="outlined"
                                fullWidth
                                onClick={() => handleTier3Selection("Fibonacci Patterns")}
                              >
                                Fibonacci patterns
                              </B2Tiny>
                            ) : (
                              <B3Tiny
                                disableRipple={Boolean(true)}
                                variant="outlined"
                                fullWidth
                                onClick={() => handleTier3Selection("Fibonacci Patterns")}
                              >
                                Fibonacci patterns
                              </B3Tiny>
                            )}
                          </Box>
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[3] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <InitialStopLossConfigure
                            indicators={indicators}
                            chartAnalyzers={chartAnalyzers}
                            onSave={(configData) => {
                              setInitialStopLoss(configData);
                            }}
                            initialData={initialStopLoss}
                            professionalMode={professionalMode}
                          />

                          {/* ---------------------------------------------------------------------- */}
                          {/* Render */}
                          {/* ---------------------------------------------------------------------- */}
                          {InitialStopLossRenderParameters(professionalMode, initialStopLoss, indicators, chartAnalyzers)}
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[4] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <B1Tiny
                              fullWidth
                              onClick={() => addStopLossTrailing()}
                              disabled={stopLossTrailings.length >= LIMITS.MAX_STOP_LOSS_TRAILINGS}
                            >
                              Add stop loss trailing
                            </B1Tiny>
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Stop loss trailings */}
                          {/* ---------------------------------------------------------------------- */}
                          {stopLossTrailings.map((item) => (
                            <Box key={item.id} variant="outlined">
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                {selectedStopLossTrailing === item.id ? (
                                  <B2Tiny
                                    disableRipple={Boolean(true)}
                                    fullWidth
                                    sx={{
                                      mr: "3px",
                                    }}
                                    onClick={() => handleStopLossTrailingSelection(item.id)}
                                  >
                                    {item?.name || 'Unnamed'}
                                  </B2Tiny>
                                ) : (
                                  <B3Tiny
                                    disableRipple={Boolean(true)}
                                    variant="outlined"
                                    fullWidth
                                    sx={{
                                      mr: "3px",
                                    }}
                                    onClick={() => handleStopLossTrailingSelection(item.id)}
                                  >
                                    {item?.name || 'Unnamed'}
                                  </B3Tiny>
                                )}

                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    setStopLossTrailingPopover({ anchorEl: event.currentTarget, itemId: item.id });
                                  }}
                                >
                                  <MoreHoriz fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}

                          {/* Stop Loss Trailing Popover */}
                          <ItemPopover
                            popoverState={stopLossTrailingPopover}
                            setPopoverState={setStopLossTrailingPopover}
                            onRemove={() => stopLossTrailingPopover.itemId && removeSpecificStopLossTrailing(stopLossTrailingPopover.itemId)}
                            onMoveUp={() =>
                              stopLossTrailingPopover.itemId && moveItemUp(stopLossTrailings, setStopLossTrailings, stopLossTrailingPopover.itemId)
                            }
                            onMoveDown={() =>
                              stopLossTrailingPopover.itemId && moveItemDown(stopLossTrailings, setStopLossTrailings, stopLossTrailingPopover.itemId)
                            }
                            onDuplicate={() => duplicateStopLossTrailing(stopLossTrailingPopover.itemId)}
                            itemName={stopLossTrailings.find((i) => i.id === stopLossTrailingPopover?.itemId)?.name || ""}
                          />
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[5] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <InitialTakeProfitConfigure
                            indicators={indicators}
                            chartAnalyzers={chartAnalyzers}
                            onSave={(configData) => {
                              setInitialTakeProfit(configData);
                            }}
                            initialData={initialTakeProfit}
                            professionalMode={professionalMode}
                          />

                          {/* ---------------------------------------------------------------------- */}
                          {/* Render */}
                          {/* ---------------------------------------------------------------------- */}
                          {InitialTakeProfitRenderParameters(professionalMode, initialTakeProfit, indicators, chartAnalyzers)}
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[6] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <B1Tiny
                              fullWidth
                              onClick={() => addTakeProfitDynamic()}
                              disabled={takeProfitDynamics.length >= LIMITS.MAX_TAKE_PROFIT_DYNAMICS}
                            >
                              Add dynamic take profit
                            </B1Tiny>
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Take profit dynamics */}
                          {/* ---------------------------------------------------------------------- */}
                          {takeProfitDynamics.map((item) => (
                            <Box key={item.id} variant="outlined">
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                {selectedTakeProfitDynamic === item.id ? (
                                  <B2Tiny
                                    disableRipple={Boolean(true)}
                                    fullWidth
                                    sx={{
                                      mr: "3px",
                                    }}
                                    onClick={() => handleTakeProfitDynamicSelection(item.id)}
                                  >
                                    {item?.name || 'Unnamed'}
                                  </B2Tiny>
                                ) : (
                                  <B3Tiny
                                    disableRipple={Boolean(true)}
                                    variant="outlined"
                                    fullWidth
                                    sx={{
                                      mr: "3px",
                                    }}
                                    onClick={() => handleTakeProfitDynamicSelection(item.id)}
                                  >
                                    {item?.name || 'Unnamed'}
                                  </B3Tiny>
                                )}

                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    setTakeProfitDynamicPopover({ anchorEl: event.currentTarget, itemId: item.id });
                                  }}
                                >
                                  <MoreHoriz fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}

                          {/* Take Profit Dynamic Popover */}
                          <ItemPopover
                            popoverState={takeProfitDynamicPopover}
                            setPopoverState={setTakeProfitDynamicPopover}
                            onRemove={() => takeProfitDynamicPopover.itemId && removeSpecificTakeProfitDynamic(takeProfitDynamicPopover.itemId)}
                            onMoveUp={() =>
                              takeProfitDynamicPopover.itemId &&
                              moveItemUp(takeProfitDynamics, setTakeProfitDynamics, takeProfitDynamicPopover.itemId)
                            }
                            onMoveDown={() =>
                              takeProfitDynamicPopover.itemId &&
                              moveItemDown(takeProfitDynamics, setTakeProfitDynamics, takeProfitDynamicPopover.itemId)
                            }
                            onDuplicate={() => duplicateTakeProfitDynamic(takeProfitDynamicPopover.itemId)}
                            itemName={takeProfitDynamics.find((i) => i.id === takeProfitDynamicPopover?.itemId)?.name || ""}
                          />
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[7] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <SymbolAndTimeframeConfigure
                            onSave={(configData) => {
                              setSymbolAndTimeframe(configData);
                            }}
                            initialData={symbolAndTimeframe}
                            professionalMode={professionalMode}
                          />

                          {/* ---------------------------------------------------------------------- */}
                          {/* Render */}
                          {/* ---------------------------------------------------------------------- */}
                          {SymbolAndTimeframeRenderParameters(professionalMode, symbolAndTimeframe)}
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[8] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <VolumeCalculationConfigure
                            indicators={indicators}
                            onSave={(configData) => {
                              setVolumeCalculation(configData);
                            }}
                            initialData={volumeCalculation}
                            professionalMode={professionalMode}
                          />

                          {/* ---------------------------------------------------------------------- */}
                          {/* Render */}
                          {/* ---------------------------------------------------------------------- */}
                          {VolumeCalculationRenderParameters(professionalMode, volumeCalculation, indicators)}
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[9] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box>
                            <B1Tiny fullWidth onClick={() => addTradeSplitting()} disabled={tradeSplittings.length >= LIMITS.MAX_TRADE_SPLITTINGS}>
                              Add trade splitting
                            </B1Tiny>
                          </Box>

                          {/* ---------------------------------------------------------------------- */}
                          {/* Trade splittings */}
                          {/* ---------------------------------------------------------------------- */}
                          {tradeSplittings.map((item) => (
                            <Box key={item.id} variant="outlined">
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                {selectedTradeSplitting === item.id ? (
                                  <B2Tiny
                                    disableRipple={Boolean(true)}
                                    fullWidth
                                    sx={{
                                      mr: "3px",
                                    }}
                                    onClick={() => handleTradeSplittingSelection(item.id)}
                                  >
                                    {item?.name || 'Unnamed'}
                                  </B2Tiny>
                                ) : (
                                  <B3Tiny
                                    disableRipple={Boolean(true)}
                                    variant="outlined"
                                    fullWidth
                                    sx={{
                                      mr: "3px",
                                    }}
                                    onClick={() => handleTradeSplittingSelection(item.id)}
                                  >
                                    {item?.name || 'Unnamed'}
                                  </B3Tiny>
                                )}

                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    setTradeSplittingPopover({ anchorEl: event.currentTarget, itemId: item.id });
                                  }}
                                >
                                  <MoreHoriz fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}

                          {/* Trade Splitting Popover */}
                          <ItemPopover
                            popoverState={tradeSplittingPopover}
                            setPopoverState={setTradeSplittingPopover}
                            onRemove={() => tradeSplittingPopover.itemId && removeSpecificTradeSplitting(tradeSplittingPopover.itemId)}
                            onMoveUp={() =>
                              tradeSplittingPopover.itemId && moveItemUp(tradeSplittings, setTradeSplittings, tradeSplittingPopover.itemId)
                            }
                            onMoveDown={() =>
                              tradeSplittingPopover.itemId && moveItemDown(tradeSplittings, setTradeSplittings, tradeSplittingPopover.itemId)
                            }
                            onDuplicate={() => duplicateTradeSplitting(tradeSplittingPopover.itemId)}
                            itemName={tradeSplittings.find((i) => i.id === tradeSplittingPopover?.itemId)?.name || ""}
                          />
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[10] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <TimingConfigure
                            onSave={(configData) => {
                              setTiming(configData);
                            }}
                            initialData={timing}
                            professionalMode={professionalMode}
                          />

                          {/* ---------------------------------------------------------------------- */}
                          {/* Render */}
                          {/* ---------------------------------------------------------------------- */}
                          {TimingRenderParameters(professionalMode, timing)}
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[11] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <MonitoringConfigure
                            onSave={(configData) => {
                              setMonitoring(configData);
                            }}
                            initialData={monitoring}
                            professionalMode={professionalMode}
                          />

                          {/* ---------------------------------------------------------------------- */}
                          {/* Render */}
                          {/* ---------------------------------------------------------------------- */}
                          {MonitoringRenderParameters(professionalMode, monitoring)}
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[12] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <DataFeedbackConfigure
                            onSave={(configData) => {
                              setDataFeedback(configData);
                            }}
                            initialData={dataFeedback}
                            professionalMode={professionalMode}
                          />

                          {/* ---------------------------------------------------------------------- */}
                          {/* Render */}
                          {/* ---------------------------------------------------------------------- */}
                          {DataFeedbackRenderParameters(professionalMode, dataFeedback)}
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[13] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <NotificationsConfigure
                            onSave={(configData) => {
                              setNotifications(configData);
                            }}
                            initialData={notifications}
                            professionalMode={professionalMode}
                          />

                          {/* ---------------------------------------------------------------------- */}
                          {/* Render */}
                          {/* ---------------------------------------------------------------------- */}
                          {NotificationsRenderParameters(professionalMode, notifications)}
                        </Stack>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      {selectedComponent === COMPONENTS[14] ? (
                        <Stack spacing={1}>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Action */}
                          {/* ---------------------------------------------------------------------- */}
                          <MiscellaneousConfigure
                            onSave={(configData) => {
                              setMiscellaneous(configData);
                            }}
                            initialData={miscellaneous}
                            professionalMode={professionalMode}
                          />

                          {/* ---------------------------------------------------------------------- */}
                          {/* Render */}
                          {/* ---------------------------------------------------------------------- */}
                          {MiscellaneousRenderParameters(professionalMode, miscellaneous)}
                        </Stack>
                      ) : null}
                    </Box>
                  </Stack>
                </Card>
              ) : null}
            </Box>

            {/* ---------------------------------------------------------------------- */}
            {/* Tier 4 */}
            {/* ---------------------------------------------------------------------- */}
            <Box
              sx={{
                flex: isMobile ? "none" : "1 0 0",
                width: isMobile ? "100%" : "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignSelf: isMobile ? "stretch" : "flex-start",
              }}
            >
              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedStopLossTrailing !== null ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title={selectedStopLossTrailingName?.name || 'Unnamed'}
                      subtitle={selectedStopLossTrailingName.subtitle}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName={selectedStopLossTrailingName?.name || 'Unnamed'}
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <StopLossTrailingsConfigure
                          indicators={indicators}
                          chartAnalyzers={chartAnalyzers}
                          initialStopLoss={initialStopLoss}
                          ref={stopLossTrailingConfigureRef}
                          onSave={(configData) => {
                            if (selectedStopLossTrailing) {
                              saveStopLossTrailingConfig(selectedStopLossTrailing, configData);
                              setStopLossTrailings((prev) =>
                                prev.map((stopLossTrailing, index) => {
                                  if (stopLossTrailing.id === selectedStopLossTrailing) {
                                    const newTitle = StopLossTrailingsBuildName(configData);
                                    const newSubtitle = StopLossTrailingsBuildSubtitle(configData);
                                    return {
                                      ...stopLossTrailing,
                                      name: newTitle,
                                      subtitle: newSubtitle,
                                    };
                                  }
                                  return stopLossTrailing;
                                })
                              );
                            }
                          }}
                          initialData={getStopLossTrailingConfig(selectedStopLossTrailing)}
                          professionalMode={professionalMode}
                        />

                        {/* ---------------------------------------------------------------------- */}
                        {/* Render */}
                        {/* ---------------------------------------------------------------------- */}
                        {StopLossTrailingsRenderParameters(
                          selectedStopLossTrailing,
                          getStopLossTrailingConfig,
                          professionalMode,
                          indicators,
                          chartAnalyzers
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}

              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedTakeProfitDynamic !== null ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title={selectedTakeProfitDynamicName?.name || 'Unnamed'}
                      subtitle={selectedTakeProfitDynamicName.subtitle}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName={selectedTakeProfitDynamicName?.name || 'Unnamed'}
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <TakeProfitDynamicsConfigure
                          indicators={indicators}
                          chartAnalyzers={chartAnalyzers}
                          initialTakeProfit={initialTakeProfit}
                          ref={takeProfitDynamicConfigureRef}
                          onSave={(configData) => {
                            if (selectedTakeProfitDynamic) {
                              saveTakeProfitDynamicConfig(selectedTakeProfitDynamic, configData);
                              setTakeProfitDynamics((prev) =>
                                prev.map((takeProfitDynamic, index) => {
                                  if (takeProfitDynamic.id === selectedTakeProfitDynamic) {
                                    const newTitle = TakeProfitDynamicsBuildName(configData);
                                    const newSubtitle = TakeProfitDynamicsBuildSubtitle(configData);
                                    return {
                                      ...takeProfitDynamic,
                                      name: newTitle,
                                      subtitle: newSubtitle,
                                    };
                                  }
                                  return takeProfitDynamic;
                                })
                              );
                            }
                          }}
                          initialData={getTakeProfitDynamicConfig(selectedTakeProfitDynamic)}
                          professionalMode={professionalMode}
                        />

                        {/* ---------------------------------------------------------------------- */}
                        {/* Render */}
                        {/* ---------------------------------------------------------------------- */}
                        {TakeProfitDynamicsRenderParameters(
                          selectedTakeProfitDynamic,
                          getTakeProfitDynamicConfig,
                          professionalMode,
                          indicators,
                          chartAnalyzers
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}

              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedTradeSplitting !== null ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title="Trade splitting"
                      subtitle={selectedTradeSplittingName?.name || 'Unnamed'}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName="Trade Splitting"
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <TradeSplittingsConfigure
                          ref={tradeSplittingConfigureRef}
                          onSave={(configData) => {
                            if (selectedTradeSplitting) {
                              saveTradeSplittingConfig(selectedTradeSplitting, configData);
                              setTradeSplittings((prev) =>
                                prev.map((tradeSplitting, index) => {
                                  if (tradeSplitting.id === selectedTradeSplitting) {
                                    const newTitle = TradeSplittingsBuildName(configData);
                                    const newSubtitle = TradeSplittingsBuildSubtitle(configData);
                                    return {
                                      ...tradeSplitting,
                                      name: newTitle,
                                      subtitle: newSubtitle,
                                    };
                                  }
                                  return tradeSplitting;
                                })
                              );
                            }
                          }}
                          initialData={getTradeSplittingConfig(selectedTradeSplitting)}
                          professionalMode={professionalMode}
                        />

                        {/* ---------------------------------------------------------------------- */}
                        {/* Render */}
                        {/* ---------------------------------------------------------------------- */}
                        {TradeSplittingsRenderParameters(selectedTradeSplitting, getTradeSplittingConfig, professionalMode)}
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}

              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedTier3 === "Indicators" ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title={selectedTier3}
                      subtitle={`${indicators.length} / ${LIMITS.MAX_INDICATORS}`}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName={selectedTier3}
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <B1Tiny fullWidth onClick={() => addIndicator()} disabled={indicators.length >= LIMITS.MAX_INDICATORS}>
                            Add new indicator
                          </B1Tiny>
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Indicators */}
                        {/* ---------------------------------------------------------------------- */}
                        {indicators.map((item) => (
                          <Box key={item.id} variant="outlined">
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              {selectedIndicator === item.id ? (
                                <B2Tiny
                                  disableRipple={Boolean(true)}
                                  fullWidth
                                  sx={{
                                    mr: "3px",
                                  }}
                                  onClick={() => handleIndicatorSelection(item.id)}
                                >
                                  {item?.name || 'Unnamed'}
                                </B2Tiny>
                              ) : (
                                <B3Tiny
                                  disableRipple={Boolean(true)}
                                  variant="outlined"
                                  fullWidth
                                  sx={{
                                    mr: "3px",
                                  }}
                                  onClick={() => handleIndicatorSelection(item.id)}
                                >
                                  {item?.name || 'Unnamed'}
                                </B3Tiny>
                              )}

                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  setIndicatorPopover({ anchorEl: event.currentTarget, itemId: item.id });
                                }}
                              >
                                <MoreHoriz fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}

                        {/* Indicator  Popover */}
                        <ItemPopover
                          popoverState={indicatorPopover}
                          setPopoverState={setIndicatorPopover}
                          onRemove={() => indicatorPopover.itemId && removeSpecificIndicator(indicatorPopover.itemId)}
                          onMoveUp={() => indicatorPopover.itemId && moveItemUp(indicators, setIndicators, indicatorPopover.itemId)}
                          onMoveDown={() => indicatorPopover.itemId && moveItemDown(indicators, setIndicators, indicatorPopover.itemId)}
                          onDuplicate={() => duplicateIndicator(indicatorPopover.itemId)}
                          itemName={indicators.find((i) => i.id === indicatorPopover?.itemId)?.name || ""}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}

              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedTier3 === "Indicator Combinations" ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title={selectedTier3}
                      subtitle={`${indicatorCombinations.length} / ${LIMITS.MAX_INDICATOR_COMBINATIONS}`}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName={selectedTier3}
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <B1Tiny
                            fullWidth
                            onClick={() => addIndicatorCombination()}
                            disabled={indicatorCombinations.length >= LIMITS.MAX_INDICATOR_COMBINATIONS}
                          >
                            Add new indicator combination
                          </B1Tiny>
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Indicator combinations */}
                        {/* ---------------------------------------------------------------------- */}
                        {indicatorCombinations.map((item) => (
                          <Box key={item.id} variant="outlined">
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              {selectedIndicatorCombination === item.id ? (
                                <B2Tiny
                                  disableRipple={Boolean(true)}
                                  fullWidth
                                  sx={{
                                    mr: "3px",
                                    whiteSpace: "pre-line",
                                  }}
                                  onClick={() => handleIndicatorCombinationSelection(item.id)}
                                >
                                  {item?.name || 'Unnamed'}
                                </B2Tiny>
                              ) : (
                                <B3Tiny
                                  disableRipple={Boolean(true)}
                                  variant="outlined"
                                  fullWidth
                                  sx={{
                                    mr: "3px",
                                    whiteSpace: "pre-line",
                                  }}
                                  onClick={() => handleIndicatorCombinationSelection(item.id)}
                                >
                                  {item?.name || 'Unnamed'}
                                </B3Tiny>
                              )}

                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  setIndicatorCombinationPopover({ anchorEl: event.currentTarget, itemId: item.id });
                                }}
                              >
                                <MoreHoriz fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}

                        {/* Indicator Combination Popover */}
                        <ItemPopover
                          popoverState={indicatorCombinationPopover}
                          setPopoverState={setIndicatorCombinationPopover}
                          onRemove={() =>
                            indicatorCombinationPopover.itemId && removeSpecificIndicatorCombination(indicatorCombinationPopover.itemId)
                          }
                          onMoveUp={() =>
                            indicatorCombinationPopover.itemId &&
                            moveItemUp(indicatorCombinations, setIndicatorCombinations, indicatorCombinationPopover.itemId)
                          }
                          onMoveDown={() =>
                            indicatorCombinationPopover.itemId &&
                            moveItemDown(indicatorCombinations, setIndicatorCombinations, indicatorCombinationPopover.itemId)
                          }
                          onDuplicate={() => duplicateIndicatorCombination(indicatorCombinationPopover.itemId)}
                          itemName={indicatorCombinations.find((i) => i.id === indicatorCombinationPopover?.itemId)?.name || ""}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}

              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedTier3 === "Chart Analyzers" ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title={selectedTier3}
                      subtitle={`${chartAnalyzers.length} / ${LIMITS.MAX_CHART_ANALYZERS}`}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName={selectedTier3}
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <B1Tiny fullWidth onClick={() => addChartAnalyzer()} disabled={chartAnalyzers.length >= LIMITS.MAX_CHART_ANALYZERS}>
                            Add new chart analyzer
                          </B1Tiny>
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Chart analyzers */}
                        {/* ---------------------------------------------------------------------- */}
                        {chartAnalyzers.map((item) => (
                          <Box key={item.id} variant="outlined">
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              {selectedChartAnalyzer === item.id ? (
                                <B2Tiny
                                  disableRipple={Boolean(true)}
                                  fullWidth
                                  sx={{
                                    mr: "3px",
                                  }}
                                  onClick={() => handleChartAnalyzerSelection(item.id)}
                                >
                                  {item?.name || 'Unnamed'}
                                </B2Tiny>
                              ) : (
                                <B3Tiny
                                  disableRipple={Boolean(true)}
                                  variant="outlined"
                                  fullWidth
                                  sx={{
                                    mr: "3px",
                                  }}
                                  onClick={() => handleChartAnalyzerSelection(item.id)}
                                >
                                  {item?.name || 'Unnamed'}
                                </B3Tiny>
                              )}

                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  setChartAnalyzerPopover({ anchorEl: event.currentTarget, itemId: item.id });
                                }}
                              >
                                <MoreHoriz fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}

                        {/* Chart Analyzer Popover */}
                        <ItemPopover
                          popoverState={chartAnalyzerPopover}
                          setPopoverState={setChartAnalyzerPopover}
                          onRemove={() => chartAnalyzerPopover.itemId && removeSpecificChartAnalyzer(chartAnalyzerPopover.itemId)}
                          onMoveUp={() => chartAnalyzerPopover.itemId && moveItemUp(chartAnalyzers, setChartAnalyzers, chartAnalyzerPopover.itemId)}
                          onMoveDown={() =>
                            chartAnalyzerPopover.itemId && moveItemDown(chartAnalyzers, setChartAnalyzers, chartAnalyzerPopover.itemId)
                          }
                          onDuplicate={() => duplicateChartAnalyzer(chartAnalyzerPopover.itemId)}
                          itemName={chartAnalyzers.find((i) => i.id === chartAnalyzerPopover?.itemId)?.name || ""}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}

              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedTier3 === "News" ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title={selectedTier3}
                      subtitle={selectedModuleName ? selectedModuleName.name : ""}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName={selectedTier3}
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <NewsConfigure
                          onSave={(configData) => {
                            setNews(configData);
                          }}
                          initialData={news}
                          professionalMode={professionalMode}
                        />

                        {/* ---------------------------------------------------------------------- */}
                        {/* Render */}
                        {/* ---------------------------------------------------------------------- */}
                        {NewsRenderParameters(professionalMode, news)}
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}

              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedTier3 === "Candle Patterns" ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title={selectedTier3}
                      subtitle={selectedModuleName ? selectedModuleName.name : ""}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName={selectedTier3}
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <CandlePatternsConfigure
                          onSave={(configData) => {
                            setCandlePatterns(configData);
                          }}
                          initialData={candlePatterns}
                          professionalMode={professionalMode}
                        />

                        {/* ---------------------------------------------------------------------- */}
                        {/* Render */}
                        {/* ---------------------------------------------------------------------- */}
                        {CandlePatternsRenderParameters(professionalMode, candlePatterns)}
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}

              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedTier3 === "Chart Patterns" ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title={selectedTier3}
                      subtitle={selectedModuleName ? selectedModuleName.name : ""}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName={selectedTier3}
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <ChartPatternsConfigure
                          onSave={(configData) => {
                            setChartPatterns(configData);
                          }}
                          initialData={chartPatterns}
                          professionalMode={professionalMode}
                        />

                        {/* ---------------------------------------------------------------------- */}
                        {/* Render */}
                        {/* ---------------------------------------------------------------------- */}
                        {ChartPatternsRenderParameters(professionalMode, chartPatterns)}
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}

              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedTier3 === "Fibonacci Patterns" ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title={selectedTier3}
                      subtitle={selectedModuleName ? selectedModuleName.name : ""}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName={selectedTier3}
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <FibonacciPatternsConfigure
                          onSave={(configData) => {
                            setFibonacciPatterns(configData);
                          }}
                          initialData={fibonacciPatterns}
                          professionalMode={professionalMode}
                        />

                        {/* ---------------------------------------------------------------------- */}
                        {/* Render */}
                        {/* ---------------------------------------------------------------------- */}
                        {FibonacciPatternsRenderParameters(professionalMode, fibonacciPatterns)}
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}
            </Box>

            {/* ---------------------------------------------------------------------- */}
            {/* Tier 5 */}
            {/* ---------------------------------------------------------------------- */}
            <Box
              sx={{
                flex: isMobile ? "none" : "1 0 0",
                width: isMobile ? "100%" : "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignSelf: isMobile ? "stretch" : "flex-start",
              }}
            >
              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedIndicator !== null ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title={selectedIndicatorName?.name || 'Unnamed'}
                      subtitle={selectedIndicatorName.subtitle}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName={selectedIndicatorName?.name || 'Unnamed'}
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <IndicatorsConfigure
                          ref={indicatorConfigureRef}
                          onSave={(configData) => {
                            if (selectedIndicator) {
                              saveIndicatorConfig(selectedIndicator, configData);
                              setIndicators((prev) =>
                                prev.map((indicator, index) => {
                                  if (indicator.id === selectedIndicator) {
                                    const newTitle = IndicatorsBuildName(configData);
                                    const newSubtitle = IndicatorsBuildSubtitle(configData);
                                    return {
                                      ...indicator,
                                      name: newTitle,
                                      subtitle: newSubtitle,
                                    };
                                  }
                                  return indicator;
                                })
                              );
                            }
                          }}
                          initialData={getIndicatorConfig(selectedIndicator)}
                          professionalMode={professionalMode}
                          indicators={indicators}
                        />

                        {/* ---------------------------------------------------------------------- */}
                        {/* Render */}
                        {/* ---------------------------------------------------------------------- */}
                        {IndicatorsRenderParameters(selectedIndicator, getIndicatorConfig, professionalMode)}
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}

              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedIndicatorCombination !== null ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title={selectedIndicatorCombinationName?.name || 'Unnamed'}
                      subtitle={selectedIndicatorCombinationName.subtitle}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName={selectedIndicatorCombinationName?.name || 'Unnamed'}
                      touched={touched}
                      errors={errors}
                    />

                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <IndicatorCombinationsConfigure
                          ref={indicatorCombinationConfigureRef}
                          onSave={(configData) => {
                            if (selectedIndicatorCombination) {
                              saveIndicatorCombinationConfig(selectedIndicatorCombination, configData);
                              setIndicatorCombinations((prev) =>
                                prev.map((indicatorCombination, index) => {
                                  if (indicatorCombination.id === selectedIndicatorCombination) {
                                    const newTitle = IndicatorCombinationsBuildName(configData, indicators);
                                    const newSubtitle = IndicatorCombinationsBuildSubtitle(configData);
                                    return {
                                      ...indicatorCombination,
                                      name: newTitle,
                                      subtitle: newSubtitle,
                                    };
                                  }
                                  return indicatorCombination;
                                })
                              );
                            }
                          }}
                          initialData={getIndicatorCombinationConfig(selectedIndicatorCombination)}
                          professionalMode={professionalMode}
                          indicators={indicators}
                        />

                        {/* ---------------------------------------------------------------------- */}
                        {/* Render */}
                        {/* ---------------------------------------------------------------------- */}
                        {IndicatorCombinationsRenderParameters(
                          selectedIndicatorCombination,
                          getIndicatorCombinationConfig,
                          professionalMode,
                          indicators
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}

              {/* ---------------------------------------------------------------------- */}
              {/* Card */}
              {/* ---------------------------------------------------------------------- */}
              {selectedChartAnalyzer !== null ? (
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  <Stack>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Head */}
                    {/* ---------------------------------------------------------------------- */}
                    <CardHeader
                      title={selectedChartAnalyzerName?.name || 'Unnamed'}
                      subtitle={selectedChartAnalyzerName.subtitle}
                      helpReference="ExpertAdvisorBuilder"
                      helpText=""
                      helpLink=""
                      titleVariant="h5"
                      fieldName={selectedChartAnalyzerName?.name || 'Unnamed'}
                      touched={touched}
                      errors={errors}
                    />
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        maxWidth: isMobile ? "calc(100vw - 24px)" : "calc(20vw - 24px)",
                      }}
                    >
                      {/* ---------------------------------------------------------------------- */}
                      {/* Content */}
                      {/* ---------------------------------------------------------------------- */}
                      <Stack spacing={1}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Action */}
                        {/* ---------------------------------------------------------------------- */}
                        <ChartAnalyzersConfigure
                          ref={chartAnalyzerConfigureRef}
                          onSave={(configData) => {
                            if (selectedChartAnalyzer) {
                              saveChartAnalyzerConfig(selectedChartAnalyzer, configData);
                              setChartAnalyzers((prev) =>
                                prev.map((chartAnalyzer, index) => {
                                  if (chartAnalyzer.id === selectedChartAnalyzer) {
                                    const newTitle = ChartAnalyzersBuildName(configData);
                                    const newSubtitle = ChartAnalyzersBuildSubtitle(configData);
                                    return {
                                      ...chartAnalyzer,
                                      name: newTitle,
                                      subtitle: newSubtitle,
                                    };
                                  }
                                  return chartAnalyzer;
                                })
                              );
                            }
                          }}
                          initialData={getChartAnalyzerConfig(selectedChartAnalyzer)}
                          professionalMode={professionalMode}
                        />

                        {/* ---------------------------------------------------------------------- */}
                        {/* Render */}
                        {/* ---------------------------------------------------------------------- */}
                        {ChartAnalyzersRenderParameters(selectedChartAnalyzer, getChartAnalyzerConfig, professionalMode)}
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              ) : null}
            </Box>
          </Box>
        </FormikProvider>
      </Box>
    ) : null;
  });
}
